// English translations
export const en = {
  // Common
  common: {
    loading: "Loading...",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    confirm: "Confirm",
    save: "Save",
    delete: "Delete",
    remove: "Remove",
    edit: "Edit",
    view: "View",
    add: "Add",
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    yes: "Yes",
    no: "No",
    at: "at",
    or: "or",
    and: "and",
    email: "Email",
    password: "Password",
    name: "Name",
    phone: "Phone",
    date: "Date",
    type: "Type",
    status: "Status",
    page: "Page",
    actions: "Actions",
    settings: "Settings",
    preferences: "Preferences",
    language: "Language",
    languageCode: "en",
    day: "day",
    days: "days",
    hour: "hour",
    hours: "hours",
    minutes: "minutes",
    week: "week",
    weeks: "weeks",
    month: "month",
    months: "months",
    inactive: "Inactive",
    sending: "Sending...",
    copy: "Copy",
    more: "more",
    tomorrow: "Tomorrow",
    refresh: "Refresh",
    menu: "Menu",
    clearFilters: "Clear"
  },
  
  // Pagination
  pagination: {
    showing: "Showing",
    to: "to",
    of: "of",
    results: "results"
  },
  
  // Days of the week
  days: {
    monday: "Monday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
    thursday: "Thursday",
    friday: "Friday",
    saturday: "Saturday",
    sunday: "Sunday",
    // Short versions
    mon: "Mon",
    tue: "Tue",
    wed: "Wed",
    thu: "Thu",
    fri: "Fri",
    sat: "Sat",
    sun: "Sun"
  },

  // Navigation - Public Header
  navigation: {
    home: "Home",
    about: "About Us",
    features: "Features",
    pricing: "Pricing",
    contact: "Contact",
    login: "Login",
    signup: "Sign Up",
    dashboard: "Dashboard"
  },

  brand: {
    slogan: "Less sorting. More talent."
  },

  industries: {
    technology: "Technology",
    financial_services: "Financial services",
    healthcare_life_sciences: "Healthcare & life sciences",
    education: "Education",
    retail_ecommerce: "Retail & e-commerce",
    manufacturing: "Manufacturing",
    professional_services_consulting: "Professional services & consulting",
    media_marketing_advertising: "Media, marketing & advertising",
    telecommunications: "Telecommunications",
    transportation_logistics: "Transportation & logistics",
    energy_utilities: "Energy & utilities",
    government_public_sector: "Government & public sector",
    non_profit: "Non-profit",
    real_estate_construction: "Real estate & construction",
    hospitality_travel: "Hospitality & travel",
    legal: "Legal services",
    human_resources_staffing: "Human resources & staffing",
    other: "Other"
  },

  // Language Selector
  languages: {
    french: "French",
    english: "English",
    spanish: "Spanish",
    selectLanguage: "Select Language"
  },

  // About Page
  about: {
    title: "About Candio",
    subtitle: "The AI solution to automate your candidate selection",
    content: {
      introduction: "Candio is an innovative SaaS platform that revolutionizes the recruitment process by using artificial intelligence to automatically analyze and compare CVs against job offers."
    },
    mission: {
      title: "Our Mission",
      description: "We help IT service companies and HR firms save time and improve the quality of their recruitment through objective and accurate analysis of applications."
    }
  },

  // Home Page
  home: {
    hero: {
      badge: "#1 Solution for IT HR Teams",
      complianceBadge: "AI Act Proof • GDPR Compliant",
      title: "Stop sorting CVs.",
      titleHighlight: "Start shortlisting the right candidates.",
      subtitle: "We handle the first screening.",
      subtitleHighlight: "You focus on hiring the right people.",
      ctaBeta: "Get beta access",
      ctaPricing: "View pricing",
      guarantees: {
        instant: "Instant setup",
        noCard: "No credit card required",
        support: "Support included"
      },
      whiteboard: {
        notes: {
          screening: {
            title: "Morning shortlist",
            body: "Review top 5 CVs before 10:00 and send shortlist to hiring manager."
          },
          followup: {
            title: "Candidate follow-up",
            body: "Call no-show candidates and confirm interview availability for tomorrow."
          },
          interview: {
            title: "Interview prep",
            body: "Align scorecard criteria with team before afternoon interviews."
          },
          feedback: {
            title: "Feedback loop",
            body: "Collect interview feedback within 2 hours to keep pipeline moving."
          },
          pipeline: {
            title: "Pipeline review",
            body: "Check offer pipeline and move blocked profiles to the right stage."
          },
          sla: {
            title: "Response SLA",
            body: "Keep candidate response time under 24h for all active applications."
          },
          sourcing: {
            title: "Daily sourcing",
            body: "Identify fresh qualified profiles to feed the active hiring pipeline."
          },
          sync: {
            title: "HR sync",
            body: "Align hiring priorities with managers at the start of the day."
          },
          calendar: {
            title: "Calendar block",
            body: "Reserve interview slots early to avoid scheduling bottlenecks."
          },
          debrief: {
            title: "Interview debrief",
            body: "Capture panel feedback right after each interview."
          },
          response: {
            title: "Candidate response",
            body: "Acknowledge applications quickly and keep communication active."
          },
          pipelineHealth: {
            title: "Pipeline health",
            body: "Detect blocked stages and rebalance workload across offers."
          }
        }
      },
      demo: {
        analyzing: "AI analysis in progress...",
        cvCount: "150 CVs",
        completed: "Analysis completed in 3 min 24s",
        timeSaved: "90% time saved",
        recommended: "Recommended",
        toConsider: "To consider",
        aiScore: "AI score 9.2/10",
        matchRate: "Match rate 92%"
      },
      visual: {
        title: "Matching engine",
        offerTitle: "Offer",
        offerRole: "Data Engineer",
        scoreLabel: "Global fit score",
        matchLabel: "Offer vs. candidates",
        breakdown: {
          skills: "Skills",
          experience: "Experience",
          culture: "Culture fit"
        },
        candidatesTitle: "Top candidates",
        candidates: {
          one: "Amel",
          two: "Karim",
          three: "Lina",
          role1: "Senior Data Engineer",
          role2: "ML Engineer",
          role3: "Data Analyst"
        }
      }
    },
    painPoints: {
      badge: "Daily challenges of HR teams",
      title: "Do you recognize yourself in these situations?",
      subtitle: "80% of HR professionals waste precious time on repetitive tasks instead of focusing on human interaction",
      timeWasting: {
        title: "Time-consuming manual sorting",
        description: "You spend 80% of your time manually sorting CVs instead of focusing on interviews and candidate relationships."
      },
      bias: {
        title: "Risk of unconscious bias",
        description: "Manual selection can introduce involuntary biases that harm the diversity and equity of your recruitment."
      },
      inconsistency: {
        title: "Non-standardized process",
        description: "Each recruiter applies their own criteria, creating inconsistencies in candidate evaluation."
      },
      tracking: {
        title: "Difficulty tracking KPIs",
        description: "Impossible to measure the effectiveness of your HR processes and optimize your recruitment performance."
      },
      preview: {
        fileTitle: "candidate-brief.docx",
        documentType: "DOCX Document",
        context: "HR operational context",
        noteLabel: "Field impact",
        lastUpdated: "Updated: today",
        signature: "Daily HR note — candio"
      },
      conclusion: "What if you could transform these challenges into competitive advantages?",
      solution: "Candio automates sorting to free up your time and creativity"
    },
    process: {
      badge: "Automated and intelligent workflow",
      title: "6 steps to revolutionize your recruitment",
      subtitle: "A simple and fluid process that eliminates 90% of repetitive tasks",
      before: "Before",
      beforeTime: "8h of work",
      now: "Now",
      nowTime: "6 minutes",
      steps: {
        connection: {
          title: "Login & Job Creation",
          description: "Log in and create your job offer with specific criteria. No more complex templates.",
          time: "3 min",
          oldWay: "❌ Manual writing, copy-paste, multiple platforms",
          features: {
            secure: "Secure interface",
            fast: "Quick creation",
            ready: "Ready in 3 min"
          }
        },
        sharing: {
          title: "Publication & Sharing",
          description: "Publish and share your offer with one click. Unique URL automatically generated for all your channels.",
          time: "30 sec",
          oldWay: "❌ Manual publication on each job site",
          features: {
            link: "Unique link generated",
            multichannel: "Multi-channel sharing",
            instant: "Instant publication"
          }
        },
        reception: {
          title: "Centralized Reception",
          description: "All applications arrive in one place. No more scattered email boxes or lost files.",
          time: "Automatic",
          oldWay: "❌ Scattered emails, manual downloads",
          features: {
            centralized: "Centralized reception",
            notifications: "Real-time notifications",
            organization: "Automatic organization"
          }
        },
        analysis: {
          title: "AI Analysis & Ranking",
          description: "AI automatically analyzes and ranks all CVs by relevance order. From most recommended to least suitable.",
          time: "2 min for 100 CVs",
          oldWay: "❌ 8h of manual reading, risk of errors",
          features: {
            ai: "AI analyzes CVs",
            score: "Compatibility score",
            ranking: "Automatic ranking"
          }
        },
        generation: {
          title: "Candidate Sheet Generation",
          description: "Transform the recommended candidate's CV into a structured sheet with AI. All key information extracted.",
          time: "10 sec",
          oldWay: "❌ Manual entry, reformatting, copy-paste",
          features: {
            extraction: "Data extraction",
            summary: "Smart summary",
            complete: "Complete candidate sheet"
          }
        },
        tracking: {
          title: "Kanban Tracking",
          description: "Send the sheet to the Kanban board and manage the recruitment process until hiring.",
          time: "1 click",
          oldWay: "❌ Excel sheets, manual follow-up emails",
          features: {
            kanban: "Visual Kanban board",
            follow: "Process tracking",
            hire: "Until hiring"
          }
        }
      },
      comparison: {
        title: "Summary: Your time savings",
        subtitle: "For a recruitment process of 100 applications",
        traditional: "❌ Traditional Method",
        candio: "✅ With Candio",
        tasks: {
          creation: "Job creation + publication",
          collection: "Application collection", 
          automated: "Automated collection",
          sorting: "Reading + sorting 100 CVs",
          aiSorting: "AI analysis + sorting 100 CVs",
          sheets: "Candidate sheet creation",
          aiSheets: "AI sheet generation",
          organization: "Organization + tracking",
          kanbanSend: "Send to Kanban"
        },
        total: "TOTAL",
        gain: "Gain: 13h 54min",
        percentage: "That's 99.3% time saved!",
        cta: "Test this workflow now"
      },
      traditionalMethod: "Traditional method:",
      // Process Simplified
      simplified: {
        badge: "Optimized process",
        title: "Simplify your recruitment in 4 steps",
        subtitle: "Focus on what matters: meeting the best candidates",
        traditional: "Traditional method",
        traditionalTime: "14 hours",
        withCandio: "With Candio",
        candioTime: "6 minutes",
        savings: "Time saved:",
        upload: {
          title: "Create and publish",
          description: "Create your job offer and share it in just a few clicks"
        },
        share: {
          title: "Collect easily",
          description: "Receive and centralize all applications automatically"
        },
        analyze: {
          title: "Analyze with AI",
          description: "Get an objective ranking of candidates in 2 minutes"
        },
        select: {
          title: "Select and track",
          description: "Manage your selected candidates in a visual board"
        },
        cta: "Ready to transform your recruitment process?",
        ctaButton: "Beta access"
      }
    },
    // Appointments Section
    appointments: {
      badge: "New feature",
      title: "Automate your appointment scheduling",
      subtitle: "Connect your favorite video conferencing tools and let candidates book their slots",
      features: {
        calendar: {
          title: "Smart calendar",
          description: "Set your availability and let candidates choose"
        },
        candidates: {
          title: "Automatic invitations",
          description: "Send personalized invitations with one click"
        },
        video: {
          title: "Multi-platform",
          description: "Compatible with your favorite video tools"
        }
      },
      howItWorks: {
        title: "How does it work?",
        step1: {
          title: "Configure your tools",
          description: "Connect Teams, Zoom, Meet or Slack"
        },
        step2: {
          title: "Set your slots",
          description: "Indicate your availability"
        },
        step3: {
          title: "Let the magic happen",
          description: "Candidates book and receive the link automatically"
        }
      },
      cta: "Discover appointment scheduling"
    },
    features: {
      title: "Features designed for HR",
      subtitle: "Each feature addresses a concrete need in your daily routine",
      aiAnalysis: {
        title: "Objective AI Analysis",
        description: "Eliminate bias with standardized evaluation based on precise and measurable business criteria.",
        benefit: "95% accuracy"
      },
      timeGain: {
        title: "Massive Time Savings",
        description: "Process 500+ CVs per hour vs 20 manually. Focus on human interaction, not sorting.",
        benefit: "90% time saved"
      },
      collaboration: {
        title: "Fluid HR Collaboration",
        description: "Interactive Kanban, shared notes and unified workflow for an aligned and efficient HR team.",
        benefit: "100% visibility"
      },
      datadriven: {
        title: "Data-Driven Management",
        description: "Real-time dashboards to measure your HR KPIs and optimize your processes.",
        benefit: "Proven ROI"
      }
    },
    roi: {
      badge: "ROI proven from the first month",
      title: "Measurable benefits for your HR team",
      subtitle: "Our clients see immediate return on investment with quantifiable gains",
      stats: {
        timesSaved: "Sorting time saved",
        cvsHour: "CVs analyzed/hour",
        precision: "Analysis precision",
        availability: "Availability"
      },
      benefits: {
        measurableRoi: {
          title: "Measurable ROI",
          description: "70% reduction in cost per hire thanks to automated sorting.",
          value: "70% savings"
        },
        quality: {
          title: "Superior Quality",
          description: "More precise selection with objective scoring and bias elimination.",
          value: "40% better matches"
        },
        timeToHire: {
          title: "Optimized Time-to-Hire",
          description: "Drastic reduction in time between publication and first selection.",
          value: "60% faster"
        },
        satisfaction: {
          title: "Team Satisfaction",
          description: "Your HR team focuses on human interaction and strategy, not sorting.",
          value: "85% satisfaction"
        }
      },
      calculator: {
        title: "Calculate your ROI in 30 seconds",
        subtitle: "How much time does your team spend sorting CVs each week? Discover your potential savings with Candio.",
        ctaCalculate: "Calculate my ROI",
        ctaDemo: "Request a demo",
        roiValue: "3.2x",
        roiDescription: "Average ROI seen from the first month"
      }
    },
    testimonials: {
      badge: "Verified customer testimonials",
      title: "They transformed their recruitment",
      subtitle: "Discover how HR teams use Candio to revolutionize their processes",
      testimonial1: {
        name: "Marie Dubois",
        role: "Directrice RH",
        company: "TechCorp Solutions",
        content: "Candio revolutionized our recruitment process. We now handle 10 times more applications with the same HR team. ROI is evident from the first month.",
        result: "300% more efficiency"
      },
      testimonial2: {
        name: "Pierre Martin", 
        role: "Consultant Senior",
        company: "HR Excellence",
        content: "The AI analysis is impressively accurate. Our clients are delighted with the quality of pre-selections. No more bias, no more errors.",
        result: "95% accuracy"
      },
      testimonial3: {
        name: "Sophie Chen",
        role: "Responsable Recrutement",
        company: "DevTech Partners",
        content: "Before Candio, we spent 80% of our time sorting. Now, we focus on human interaction and HR strategy.",
        result: "80% time freed"
      },
      stats: {
        rating: "Average rating",
        customers: "Satisfied customers",
        retention: "Retention rate"
      }
    },
    community: {
      badge: "Public launch community",
      title: "Support Candio during our launch phase",
      subtitle: "Candio is currently in test phase. Join the list to receive launch updates and support a 100% Quebec-built solution.",
      points: {
        testing: "Early access to launch announcements and product milestones.",
        support: "Directly support the product by joining our growing community.",
        quebec: "A proudly 100% Quebec-built HR technology solution."
      },
      form: {
        label: "Join the launch list",
        placeholder: "name@company.com",
        cta: "Join the community",
        loading: "Joining...",
        success: "Thanks! Your email has been added to the launch list.",
        error: "Unable to add your email right now. Please try again later.",
        invalidEmail: "Please enter a valid email address.",
        disclaimer: "We only send important launch updates. No spam."
      }
    },
    cta: {
      badge: "Open beta program",
      title: "Ready to free up 90% of your time",
      titleHighlight: "from CV sorting?",
      subtitle: "Join HR teams that have already transformed their recruitment.",
      subtitleHighlight: "5-minute setup • Results from the first week",
      button: "Get beta access",
      guarantees: {
        trial: "7 days free, no commitment",
        instant: "Instant configuration", 
        support: "Dedicated support included"
      }
    },
    footer: {
      description: "Less sorting. More talent.",
      product: {
        title: "Product",
        features: "Features",
        pricing: "Sign up",
        api: "API"
      },
      company: {
        title: "Company",
        about: "About",
        contact: "Contact",
        careers: "Careers"
      },
      support: {
        title: "Support",
        documentation: "Documentation",
        guides: "Guides",
        security: "Security and privacy"
      },
      copyright: "&copy; 2025 Candio. All rights reserved."
    }
  },

  // Features Page
  features: {
    title: "Powerful Features for Modern Recruitment",
    subtitle: "Everything you need to streamline your hiring process",
    
    // Hero section
    heroTitle: "Features",
    heroTitleHighlight: "Innovative",
    heroSubtitle: "Discover a complete suite of recruitment tools designed to simplify and optimize your talent selection process.",
    
    // Main features sections
    needsTitle: "Everything you need",
    needsSubtitle: "A comprehensive platform that covers all aspects of modern recruitment",
    whyChooseTitle: "Why choose our solution?",
    whyChooseSubtitle: "Concrete advantages that transform the way you recruit",
    processTitle: "A simplified process",
    processSubtitle: "From job posting to hiring, an optimized workflow",
    ctaTitle: "Ready to transform your recruitment?",
    ctaSubtitle: "Join companies that have already revolutionized their recruitment process",
    ctaStartNow: "Beta access",
    ctaContact: "Contact us",
    
    // Database feature
    database: {
      title: "Centralized CV Database",
      description: "Organize and manage your candidate database in a unified space with advanced search tools.",
      benefits: {
        import: "Import multiple",
        organization: "Smart organization",
        search: "Quick search"
      }
    },
    
    // Offer management
    offerManagement: {
      title: "Job Offer Management",
      description: "Create, publish and manage your job offers with modern distribution and tracking tools.",
      benefits: {
        creation: "Simplified creation",
        publication: "Multi-channel publication",
        tracking: "Performance tracking"
      }
    },
    
    // Application centralization
    applicationCentralization: {
      title: "Application Centralization",
      description: "Automatically collect all applications and manage them from a unified interface.",
      benefits: {
        collection: "Collection automatique",
        sorting: "Tri intelligent",
        notifications: "Real-time notifications"
      }
    },
    
    // Recruitment tracking
    recruitmentTracking: {
      title: "Recruitment Tracking",
      description: "Visualize and manage the recruitment process with intuitive collaborative tools.",
      benefits: {
        visual: "Interface visuelle",
        collaboration: "Team collaboration",
        tracking: "Stage tracking"
      }
    },
    
    // Notes management
    notesManagement: {
      title: "Notes Management",
      description: "Share notes and comments with your team for optimal collaboration.",
      benefits: {
        shared: "Shared notes",
        realtime: "Real-time collaboration",
        history: "Complete history"
      }
    },
    
    // AI Matching (existing)
    aiMatching: {
      title: "AI Comparator",
      description: "Analyze and compare applications with advanced algorithms to optimize your decisions.",
      benefits: {
        automatic: "Automatic analysis",
        intelligent: "Smart comparison",
        decision: "Decision support"
      }
    },
    
    // Appointment booking
    appointmentBooking: {
      title: "Appointment Booking",
      description: "Simplify interview scheduling with a Calendly-like online booking system integrated for HR teams.",
      benefits: {
        calendar: "Smart calendar",
        automation: "Automatic booking",
        videoIntegration: "Integrated video"
      }
    },
    
    // Benefits section
    benefits: {
      timeGain: {
        title: "Time Savings",
        description: "Automate repetitive tasks and focus on human interaction"
      },
      precision: {
        title: "Increased Precision",
        description: "Find the best candidates through artificial intelligence"
      },
      security: {
        title: "Guaranteed Security",
        description: "Your data is protected with the highest security standards"
      },
      accessibility: {
        title: "Accessibility",
        description: "Access your platform from anywhere, anytime"
      }
    },
    
    // Stats section
    stats: {
      timeReduction: "Sorting time reduction",
      precisionImprovement: "Precision improvement",
      processAcceleration: "Process acceleration",
      userSatisfaction: "Satisfaction utilisateur"
    },
    outcomes: {
      title: "Measured outcomes",
      subtitle: "Concrete proof points from daily usage"
    },
    
    // Process steps
    processSteps: {
      publication: {
        title: "Publication",
        description: "Create and distribute your offers"
      },
      collection: {
        title: "Collection",
        description: "Receive applications"
      },
      analysis: {
        title: "Analyse",
        description: "Evaluate with AI"
      },
      selection: {
        title: "Selection",
        description: "Choose the best"
      }
    },
    
    // Legacy features for backward compatibility
    batchAnalysis: {
      title: "Batch Analysis",
      description: "Upload and analyze multiple CVs at once. Get comprehensive reports comparing all candidates simultaneously."
    },
    kanbanBoard: {
      title: "Kanban Board Management",
      description: "Organize candidates in your recruitment pipeline with our intuitive drag-and-drop interface."
    },
    teamCollaboration: {
      title: "Team Collaboration",
      description: "Share candidate profiles, add notes and collaborate with colleagues throughout the hiring process."
    },
    customBranding: {
      title: "Custom Branding",
      description: "Customize the platform with your company logo and colors for a professional experience."
    }
  },

  // Pricing Page
  pricing: {
    title: "Choose Your Plan",
    subtitle: "Flexible pricing that grows with your business",
    monthly: "Monthly",
    yearly: "Yearly",
    save: "Save 20%",
    popular: "Le Plus Populaire",
    free: "Free",
    planNames: {
      demo: "Demo Free",
      essential: "Essential HR",
      enterprise: "Enterprise"
    },
    demo: {
      name: "Demo",
      price: "Gratuit",
      duration: "",
      description: "Perfect for trying our platform",
      features: [
        "10 AI analyses per month",
        "1 HR user",
        "5 job offers",
        "50 CV uploads",
        "Basic support",
        "Standard features"
      ],
      cta: "Start Free Demo"
    },
    essential: {
      name: "Essential",
      price: "49€",
      duration: "/mois",
      description: "Ideal for small teams",
      features: [
        "100 AI analyses per month",
        "2 HR users",
        "10 job offers",
        "500 CV uploads",
        "Email support",
        "Team collaboration",
        "Basic reports"
      ],
      cta: "Choose Essential"
    },
    enterprise: {
      name: "Enterprise",
      price: "149€",
      duration: "/mois",
      description: "For growing businesses",
      features: [
        "500 AI analyses per month",
        "10 HR users",
        "50 job offers",
        "2000 CV uploads",
        "Priority support",
        "Custom branding",
        "Advanced reports",
        "Custom MinIO storage"
      ],
      cta: "Choose Enterprise"
    },
    customDescription: "Custom plan for your needs",
    features: {
      title: "All plans include:",
      list: [
        "AI CV analysis",
        "Kanban board management",
        "Real-time collaboration",
        "Secure data encryption",
        "Mobile responsive interface",
        "Regular updates"
      ],
      aiAnalysis: "AI CV analysis",
      kanbanManagement: "Kanban board management",
      realtimeCollaboration: "Real-time collaboration",
      secureEncryption: "Secure data encryption",
      responsiveInterface: "Mobile responsive interface",
      regularUpdates: "Regular updates",
      batchProcessing: "Batch processing",
      exportFormats: "PDF/Excel export",
      searchFilters: "Advanced search filters",
      emailNotifications: "Email notifications",
      dashboardWidgets: "Dashboard widgets",
      multiLanguage: "Multi-language support",
      hrUsers: "HR Users included",
      hrUsersDesc: "Number of HR users who can access the system",
      jobOffers: "Active job offers",
      jobOffersDesc: "Maximum number of simultaneous job offers",
      cvUploads: "CV Uploads (internal repository)",
      cvUploadsDesc: "CV storage for your internal database",
      aiClassifier: "Smart Classifier (AI)",
      aiClassifierDesc: "AI analyses to classify and evaluate CVs",
      aiSummary: "Automatic CV Summary (AI)",
      aiSummaryDesc: "Automatic generation of CV summaries",
      appointments: "Appointment booking",
      appointmentsDesc: "Online booking system for candidates",
      new: "New",
      customStorage: "Custom MinIO Storage",
      customStorageDesc: "Use your own MinIO storage service for documents",
      videoAutomation: "Meeting URL Automation",
      videoAutomationDesc: "Automatic generation of video conference links with third-party services",
      enterprise: "Enterprise"
    },
    additionalFeatures: {
      title: "Additional features",
      demo: [
        "Limited feature access",
        "Demo data",
        "Community support"
      ],
      essential: [
        "All basic features",
        "Basic integrations",
        "Online training"
      ],
      enterprise: [
        "All Premium features",
        "Custom integrations",
        "On-site training",
        "Dedicated account manager"
      ],
      tracking: "Offer tracking and monitoring",
      applications: "Application management",
      kanban: "Kanban Board – recruitment tracking",
      notes: "Collaborative & shared notes",
      emailSupport: "Email support",
      prioritySupport: "Priority support & intervention"
    },
    currentPlan: "Current plan",
    perMonth: "mois",
    startFree: "Commencer gratuitement",
    choosePlan: "Choose this plan",
    redirecting: "Redirecting...",
    loginRequired: "Please log in to subscribe to a plan",
    demoIncluded: "The Demo package is already included with your account",
    packageNotFound: "Package not found",
    missingPaymentConfig: "Payment configuration missing for this package",
    invalidPriceConfig: "Invalid price configuration",
    subscriptionActivated: "Subscription activated successfully!",
    subscriptionError: "Error during subscription",
    premium: "Premium",
    current: "Current",
    needHelp: "Need help choosing the right plan?",
    contactUs: "Contact us",
    error: {
      load: "Error loading packages"
    },
    comparison: {
      title: "Compare our plans",
      features: "Features",
      feature: "Feature",
      aiAnalyses: "AI analyses per month",
      hrUsers: "HR users",
      jobOffers: "Job offers",
      cvUploads: "CV uploads",
      support: "Support",
      teamCollaboration: "Team collaboration",
      reports: "Reports",
      customBranding: "Custom branding",
      minioStorage: "Custom MinIO storage",
      advancedAnalytics: "Advanced analytics",
      apiAccess: "API access",
      sla: "Guaranteed SLA",
      included: "Included",
      notIncluded: "-",
      basic: "Basic",
      email: "Email",
      priority: "Priority",
      advanced: "Advanced",
      custom: "Custom"
    },
    faq: {
      title: "Frequently Asked Questions",
      subtitle: "Everything you need to know about our plans",
      changePlan: {
        question: "Can I change plans at any time?",
        answer: "Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately."
      },
      limits: {
        question: "What happens if I exceed my limits?",
        answer: "Your access will be temporarily suspended until your subscription renewal or upgrade to a higher plan."
      },
      hrUsers: {
        question: "Do HR users have access to the same features?",
        answer: "Yes, HR users created by an administrator automatically inherit the administrator's subscription privileges."
      },
      cancel: {
        question: "Can I cancel my subscription?",
        answer: "Yes, you can cancel your subscription at any time. You will retain access until the end of your billing period."
      },
      appointments: {
        question: "How do appointments work?",
        answer: "Appointments allow you to schedule interviews directly with selected candidates. You can manage your calendar, send invitations, and track confirmations from your dashboard."
      },
      trial: {
        question: "Is there a free trial period?",
        answer: "Yes, our Demo plan offers free access with 10 AI analyses per month to test all platform features without commitment."
      }
    }
  },

  // Contact Page
  contact: {
    title: "Contact Us",
    subtitle: "We're here to help",
    hero: {
      title: "Contact Our Team",
      subtitle: "Have a question? Need help? Our team of experts is here to support you in your HR digital transformation."
    },
    form: {
      title: "Send us a message",
      subtitle: "Fill out the form below and we'll respond quickly.",
      name: "Full name",
      namePlaceholder: "Your full name",
      email: "Email address",
      emailPlaceholder: "your.email@company.com",
      company: "Company name",
      subject: "Subject of your request",
      message: "Your message",
      messagePlaceholder: "Describe your request in detail...",
      characters: "characters",
      send: "Send message",
      sending: "Sending...",
      success: "Message sent successfully! We'll respond within 24h.",
      errors: {
        nameRequired: "Name is required",
        emailRequired: "Email is required",
        emailInvalid: "Invalid email",
        messageRequired: "Message is required",
        messageTooShort: "Message must contain at least 10 characters",
        formValidation: "Please correct the errors in the form",
        sendError: "Error sending message",
        connectionError: "Connection error. Please try again or contact us directly at hello@candio.ai"
      }
    },
    success: {
      title: "Message sent!",
      message: "Thank you for your message! We'll respond soon."
    },
    subjects: {
      support: {
        label: "Technical Support",
        description: "Help, technical questions, issues"
      },
      sales: {
        label: "Demo & onboarding", 
        description: "Demo, onboarding, general questions"
      },
      general: {
        label: "General Question",
        description: "Information, partnership, other"
      }
    },
    info: {
      title: "Contact Information",
      email: {
        title: "Email",
        description: "We respond within 24h"
      },
      hours: {
        title: "Support Hours",
        value: "Mon-Fri 9am-6pm",
        description: "French time (GMT+1)"
      },
      multilingual: {
        title: "Multilingual Support",
        value: "FR / EN",
        description: "French and English"
      },
      responseTime: "We typically respond within 24 business hours."
    },
    why: {
      title: "Why contact us?",
      demo: {
        title: "Personalized demo",
        description: "Discover Candio tailored to your needs"
      },
      support: {
        title: "Expert support",
        description: "Technical help and guidance"
      },
      pricing: {
        title: "Custom onboarding",
        description: "Onboarding and guidance tailored to your organization"
      },
      training: {
        title: "Team training",
        description: "Onboarding and user training"
      }
    },
    response: {
      title: "Guaranteed Response",
      subtitle: "We commit to responding to all messages",
      sales: "Questions vente",
      support: "Support technique"
    },
    error: "An error occurred while sending your message. Please try again.",
    modal: {
      title: "Contact Us",
      subtitle: "We're here to help"
    },
    validation: {
      messageRequired: "Message is required"
    },
    errors: {
      sendFailed: "Error sending message"
    }
  },

  // Authentication
  auth: {
    common: {
      secure: "Secure"
    },
    password: {
      show: "Show password",
      hide: "Hide password"
    },
    // Validation errors
    validation: {
      invalidEmail: "Invalid email address",
      passwordMinLength: "Password must be at least 8 characters long",
      nameLength: "Name must be between 2 and 100 characters",
      passwordRequired: "Password is required",
      emailRequired: "Email address is required",
      emailInvalid: "Please enter a valid email address",
      confirmPasswordRequired: "Please confirm your password",
      passwordsDoNotMatch: "Passwords do not match",
      allFieldsRequired: "All fields are required"
    },
    // Resend verification
    resend: {
      userNotFoundOrVerified: "User not found or already verified",
      success: "Verification email sent successfully",
      failed: "Failed to send verification email"
    },
    // General auth errors
    error: {
      tooManyAttempts: "Too many authentication attempts. Please try again in {{minutes}} minutes.",
      internal: "Internal server error",
      userNotFound: "User not found"
    },
    // Email verification
    verify: {
      tokenRequired: "Verification token required",
      invalidToken: "Invalid or expired verification token",
      success: "Email verified successfully. You can now log in.",
      alreadyVerified: "Email already verified. You can log in."
    },
    // Messages
    messages: {
      connectionError: "Connection error. Please try again.",
      resendError: "Error sending email",
      verificationRequired: "Email verification required",
      signupSuccess: "Account created successfully! Please check your email inbox to verify your email address.",
      signupError: "Error creating account",
      backToHome: "Back to home",
      newToCandio: "New to Candio?",
      createAccountForTrial: "Create your account and get 2 days free trial",
      startFreeTrial: "Start free trial"
    },
    banner: {
      login: {
        title: "Welcome to Candio",
        subtitle: "The AI solution to optimize your recruitment"
      },
      signup: {
        title: "Join Candio",
        subtitle: "Start your free trial today"
      },
      features: {
        matching: {
          title: "Precise AI Matching",
          description: "Find the best candidates in seconds"
        },
        analytics: {
          title: "Advanced Analytics",
          description: "Track your recruitment performance in real-time"
        },
        collaboration: {
          title: "Team Collaboration",
          description: "Work together on your recruitment processes"
        }
      },
      stats: {
        companies: "Companies",
        cvs: "CVs analyzed",
        satisfaction: "Satisfaction"
      },
      trust: {
        secure: "Secure data",
        compliant: "GDPR Compliant"
      }
    },
    login: {
      title: "Access your workspace",
      subtitle: "Sign in to continue.",
      helperText: "Workspace language and timezone are applied at authentication.",
      email: "Email address",
      password: "Password",
      or: "or",
      forgotPassword: "Forgot password?",
      loginButton: "Sign in",
      noAccount: "Don't have an account?",
      signUp: "Sign up here",
      // Login errors
      userNotFound: "No account found with this email address.",
      incorrectPassword: "Incorrect password. Please check your password.",
      notVerified: "Account not verified",
      notVerifiedMessage: "Please verify your email before logging in. Check your inbox.",
      licenseExpired: "License expired",
      errors: {
        invalidCredentials: "Invalid email or password",
        emailRequired: "Email is required",
        emailInvalid: "Please enter a valid email address",
        passwordRequired: "Password is required",
        userNotFound: "No account found with this email address.",
        invalidPassword: "Incorrect password. Please check your password.",
        accountNotVerified: "Account not verified",
        verifyEmailFirst: "Please verify your email before logging in. Check your inbox.",
        licenseExpired: "License expired"
      }
    },
    signup: {
      title: "Create your Candio workspace",
      subtitle: "Set up your account in a few steps.",
      fullName: "Full name (optional)",
      email: "Email address",
      password: "Password",
      confirmPassword: "Confirm password",
      passwordHint: "Minimum 8 characters",
      company: "Company name",
      companyPlaceholder: "Your company name",
      industry: "Industry",
      industryPlaceholder: "Technology, Healthcare, Finance...",
      timezone: "Time zone",
      selectTimezone: "Select your time zone",
      selectIndustry: "Select your industry",
      signupButton: "Create my account",
      hasAccount: "Already have an account?",
      signIn: "Sign in",
      helperText: "Your company profile and timezone are used to configure workspace defaults.",
      terms: "By creating an account, you accept our Terms of Service and Privacy Policy",
      freeTrialTitle: "Free trial included",
      freeTrialDescription: "2 days full access • 10 analyses • All features",
      errors: {
        nameRequired: "Full name is required",
        nameTooShort: "Name must contain at least 2 characters",
        emailRequired: "Email is required",
        emailInvalid: "Please enter a valid email",
        passwordRequired: "Password is required",
        passwordTooShort: "Password must contain at least 8 characters",
        confirmPasswordRequired: "Please confirm your password",
        passwordsNoMatch: "Passwords do not match",
        companyRequired: "Company name is required",
        industryRequired: "Industry is required",
        timezoneRequired: "Time zone is required",
        emailExists: "This email address is already in use. Try logging in or use a different address.",
        signupFailed: "Error creating account",
        internalError: "Internal server error"
      },
      success: {
        accountCreated: "Account created successfully! Please check your email.",
        accountCreatedDev: "Account created successfully! (Auto-verified in development)",
        accountCreatedEmailFailed: "Account created successfully! Email verification failed but account is activated."
      },
      passwordChecks: {
        title: "Password checks",
        length: "At least 8 characters",
        uppercase: "One uppercase letter",
        lowercase: "One lowercase letter",
        number: "One number"
      }
    },
    verification: {
      title: "Email Verification",
      subtitle: "We have sent a verification link to your email address",
      checkEmail: "Please check your email and click the verification link to complete your registration.",
      checkEmailPrompt: "Your account is not yet verified. Check your email or send a new link.",
      resend: "Resend verification email",
      unverifiedAccount: "Unverified account",
      backToLogin: "Back to login",
      states: {
        verifying: "Verification in progress...",
        pleasewait: "Please wait while we verify your email.",
        success: "Email verified successfully!",
        accountActivated: "Your account has been activated. You can now log in.",
        error: "Verification error",
        invalidLink: "The verification link is invalid or has expired.",
        alreadyVerified: "Email already verified. You can log in."
      },
      actions: {
        createNewAccount: "Create new account",
        tryLogin: "Try to log in"
      },
      errors: {
        missingToken: "Verification token missing",
        verificationFailed: "Verification failed. Please try again.",
        tokenRequired: "Verification token required",
        invalidToken: "Invalid or expired verification token",
        userNotFound: "User not found or already verified",
        resendFailed: "Failed to send verification email"
      },
      messages: {
        success: "Email verified successfully!"
      },
      help: {
        needHelp: "Need help?",
        contactUs: "Contact us"
      },
      success: {
        emailSent: "Verification email sent successfully"
      }
    },
    // Forgot password
    forgotPassword: {
      title: "Forgot Password?",
      subtitle: "Enter your email and we'll send you a link to reset your password",
      emailLabel: "Email address",
      emailPlaceholder: "your@email.com",
      sendResetLink: "Send reset link",
      backToLogin: "Back to login",
      emailSentTitle: "Email sent!",
      emailSentDescription: "We sent a password reset link to {{email}}",
      checkSpam: "If you don't see the email, check your spam folder.",
      checkEmailMessage: "If an account exists with this email address, you will receive a password reset link.",
      emailSentSuccess: "Email sent successfully"
    },
    // Reset password
    resetPassword: {
      title: "Reset Password",
      subtitle: "Choose a new secure password",
      newPassword: "New password",
      confirmPassword: "Confirm password",
      resetButton: "Reset password",
      backToLogin: "Back to login",
      invalidToken: "Invalid or expired reset link",
      successTitle: "Password reset!",
      successDescription: "Your password has been reset successfully",
      successMessage: "Your password has been reset successfully. You can now log in.",
      redirectingToLogin: "Redirecting to login page..."
    }
  },

  // Dashboard
  dashboard: {
    welcome: "Hello",
    overview: "Overview",
    quickActions: "Quick Actions",
    recentActivity: "Recent Activity",
    statistics: "Statistics",
    noRecentActivity: "No recent activity",
    subtitle: {
      admin: "Here's an overview of your team's activity",
      hr: "Here's an overview of your team's shared activity"
    },
    plan: {
      current: "Plan",
      expired: "Expired",
      expiredTitle: "Plan Expired",
      expiredMessage: "Your {{planName}} plan has expired. Renew your subscription to continue using Candio."
    },
    planInfo: {
      title: "Plan Information",
      planType: "Plan",
      demo: "Demo",
      active: "Active",
      inactive: "Inactive",
      expiresOn: "Expires on",
      analyses: "Analyses",
      perMonth: "month",
      offers: "Offers",
      cvs: "CVs",
      hrUsers: "HR Users",
      activePlan: "Active Plan",
      users: "Users",
      lastSync: "Last synchronization"
    },
    workspace: {
      shared: "Shared Workspace"
    },
    roles: {
      admin: "Admin",
      hr: "HR"
    },
    actions: {
      sync: "Synchronize",
      refresh: "Refresh"
    },
    errors: {
      syncFailed: "Synchronization error",
      general: "Error",
      activitiesLoadFailed: "Error loading activities",
      notesLoadFailed: "Error loading notes",
      quickActionsLoadFailed: "Error loading quick actions",
      calendarLoadFailed: "Error loading calendar",
      preferencesLoadFailed: "Error loading dashboard preferences",
      preferencesSaveFailed: "Error saving dashboard preferences"
    },
    pagination: {
      showing: "Showing",
      to: "to",
      of: "of",
      entries: "entries",
      previous: "Previous",
      next: "Next"
    },
    
    // Navigation
    nav: {
      dashboard: "Dashboard",
      offers: "Job Offers",
      cvs: "CV Library",
      analyzer: "AI Analyzer",
      results: "Results",
      kanban: "Kanban",
      candidates: "Candidates",
      settings: "Settings",
      hrManagement: "HR Management",
      pricing: "Subscription",
      notes: "Notes",
      applications: "Applications",
      appointments: "Appointments",
      env: "Variables",
      community: "Community"
    },

    // Widgets
    widgets: {
      offers: "Job Offers",
      cvs: "CVs Uploaded",
      hrUsers: "HR Users",
      analyses: "AI Analyses",
      aiResume: "AI CV Summary",
      remaining: "{{count}} remaining",
      limitReached: "Limit reached",
      recentNotes: "Recent Notes",
      addQuickNote: "Add quick note",
      noNotes: "No notes yet",
      createFirstNote: "Create your first note above",
      shared: "Shared",
      sharedBy: "by {{name}}",
      viewAllNotes: "View all notes",
      sharedCalendar: {
        title: "Shared Calendar",
        noAppointments: "No appointments",
        viewAll: "View all appointments"
      },
      quickActions: "Quick Actions",
      basedOnUsage: "Based on your usage",
      noActionsAvailable: "No actions available",
      navigationError: "Navigation error",
      frequency: {
        once: "1 time",
        times: "{{count}} times",
        many: "10+ times"
      },
      offersData: {
        title: "Job Offers",
        open: "Open",
        closed: "Closed",
        cancelled: "Cancelled"
      },
      cvsData: {
        title: "CVs Uploaded",
        total: "Total",
        thisMonth: "This month",
        uploaded: "Uploaded"
      },
      analysesData: {
        title: "AI Analyses",
        used: "Used",
        remaining: "Remaining",
        thisMonth: "This month"
      },
      plan: {
        title: "Current Plan",
        upgrade: "Upgrade"
      },
      ai: {
        title: "AI Features",
        analysesDescription: "AI comparator and CV summaries",
        resumeDescription: "AI-powered candidate profiles",
        newBadge: "New",
        upcoming: "Upcoming AI features",
        comingSoon: "Coming soon"
      }
    },

    // Tabs
    tabs: {
      widgets: "Widgets View",
      analytics: "HR Analytics View"
    },

    // Analytics
    analytics: {
      filters: {
        last7Days: "Last 7 days",
        last30Days: "Last 30 days",
        last90Days: "Last 90 days",
        allOffers: "All offers"
      },
      error: {
        fetchFailed: "Error loading data"
      },
      export: {
        inProgress: "Export in progress...",
        success: "Export successful",
        error: "Export error"
      },
      dragDrop: {
        hint: "Drag to reorder",
        reorderSuccess: "Widgets reordered"
      },
      empty: {
        title: "No data available",
        description: "Metrics will appear here once you have data."
      },
      candidates: {
        title: "Applications Activity",
        subtitle: "Track received applications",
        total: "Total",
        new: "New",
        avgPerOffer: "Average per offer",
        applications: "Applications",
        noData: "No applications for this period"
      },
      offers: {
        title: "Offers Activity",
        subtitle: "Status of your job offers",
        active: "Active",
        closed: "Closed",
        cancelled: "Cancelled",
        popular: "Most popular offers",
        applications: "applications",
        statusDistribution: "Status Distribution",
        applicationsChart: "Applications per Offer"
      },
      ai: {
        title: "AI & Processing",
        subtitle: "Automated analyses",
        analyses: "Analyses",
        scored: "Scored",
        summaries: "Summaries",
        avgTime: "Avg. time",
        processingEfficiency: "AI Processing Efficiency",
        usageTrend: "AI Usage Trend",
        comparator: "Comparator",
        cvSummary: "CV Summary",
        scoring: "Scoring"
      },
      quality: {
        title: "Quality & Scores",
        subtitle: "Profile distribution",
        avgScore: "Average score",
        highScore: "High score",
        distribution: "Score distribution",
        candidates: "Candidates",
        noData: "No score analysis available"
      },
      history: {
        title: "History & Traceability",
        subtitle: "Actions and recent events",
        totalActions: "Actions",
        exports: "Exports",
        recent: "Recent activities",
        noData: "No recent activity",
        activityByUser: "Activity by user",
        actionTypes: "Action types"
      },
      actionTypes: {
        offer: "Offers",
        cv: "CVs",
        analysis: "Analyses",
        candidate: "Candidates",
        kanban: "Kanban"
      },
      noData: "No data"
    },

    // Activity tracking
    activity: {
      title: "Recent Activity",
      noActivity: "No activity",
      noActivities: "No recent activity",
      noRecentActivity: "No recent activity",
      newActionsHint: "New actions will appear here",
      count: "activity",
      userFallback: "User",
      fallback: "{{user}} performed an action",
      time: {
        now: "just now",
        minutesAgo: "{{count}} minute{{plural}} ago",
        hoursAgo: "{{count}} hour{{plural}} ago",
        daysAgo: "{{count}} day{{plural}} ago"
      },
      types: {
        offer_created: "Offer created",
        offer_updated: "Offer updated", 
        offer_deleted: "Offer deleted",
        offer_published: "Offer published",
        offer_unpublished: "Offer unpublished",
        cv_uploaded: "CV uploaded",
        cv_deleted: "CV deleted",
        analysis_completed: "AI analysis completed",
        analysis_reanalyzed: "AI analysis re-performed",
        result_exported: "Result exported",
        user_login: "User login",
        settings_updated: "Settings updated"
      },
      // Templates for activity descriptions
      templates: {
        // Offers
        offer_created: "created offer \"{{title}}\"",
        offer_updated: "updated offer \"{{title}}\"",
        offer_deleted: "deleted offer \"{{title}}\"",
        offer_published: "published offer \"{{title}}\"",
        offer_unpublished: "unpublished offer \"{{title}}\"",
        
        // CVs
        cv_uploaded: "uploaded CV \"{{filename}}\"",
        cv_deleted: "deleted CV \"{{filename}}\"",
        
        // Analyses
        analysis_completed: "completed analysis of {{count}} CV(s)",
        analysis_reanalyzed: "re-analyzed {{count}} CV(s)",
        
        // Candidates
        candidate_created_manual: "manually created candidate \"{{name}}\"",
        candidate_shared_to_kanban: "shared candidate \"{{name}}\" to kanban",
        candidate_updated: "updated candidate \"{{name}}\"",
        candidate_moved: "moved candidate \"{{name}}\"",
        candidate_unshared_from_kanban: "removed candidate \"{{name}}\" from kanban",
        candidate_deleted: "deleted candidate \"{{name}}\"",
        
        // Applications
        application_submitted: "Application submitted by \"{{name}}\"",
        application_viewed: "viewed application from \"{{name}}\"",
        application_status_changed: "changed application status of \"{{name}}\"",
        application_cv_downloaded: "downloaded CV of \"{{name}}\"",
        application_deleted: "deleted application from \"{{name}}\"",
        application_note_added: "added note to application from \"{{name}}\"",
        application_note_updated: "updated note for application from \"{{name}}\"",
        application_note_deleted: "deleted note from application of \"{{name}}\"",
        application_added_to_kanban: "added \"{{name}}\" to kanban",
        
        // Kanban
        kanban_column_created: "created column \"{{name}}\"",
        kanban_column_updated: "updated column \"{{name}}\"",
        kanban_column_deleted: "deleted column \"{{name}}\"",
        
        // Subscriptions
        subscription_upgraded: "upgraded to \"{{plan}}\" plan",
        subscription_downgraded: "downgraded to \"{{plan}}\" plan",
        
        // Appointments
        appointment_created: "created an appointment",
        appointment_confirmed: "confirmed appointment with \"{{name}}\"",
        appointment_cancelled: "cancelled appointment with \"{{name}}\"",
        appointment_rescheduled: "rescheduled appointment with \"{{name}}\"",
        appointment_reminded: "sent reminder for appointment with \"{{name}}\"",
        availability_created: "added availability slot",
        availability_updated: "updated availability",
        availability_deleted: "deleted availability slot",
        send_appointment_invitation: "sent appointment invitation to \"{{name}}\"",
        appointment_booked_via_url: "appointment booked via URL by \"{{name}}\"",
        booking_settings_updated: "updated booking settings",
        booking_settings_created: "configured booking settings",
        video_conference_configured: "configured video conferencing {{provider}}",
        video_conference_updated: "updated video conference configuration",
        video_conference_deleted: "deleted video conference configuration",
        holiday_created: "added holiday{{global}}: {{name}}",
        holiday_updated: "updated holiday: {{name}}",
        holiday_deleted: "deleted holiday: {{name}}",
        holidays_initialized: "initialized {{count}} default holidays",
        appointment_received: "new appointment received",
        
        // Test
        test_performed: "performed system test",
        
        // Settings
        settings_cleanup_updated: "{{user}} updated automatic cleanup settings",
        cleanup_manual_started: "{{user}} started manual cleanup",
        
        // Notes
        note_shared: "{{user}} shared note \"{{title}}\" with {{userNames}}",
        note_unshared: "{{user}} stopped sharing note \"{{title}}\"",
        
        // Email/Video configuration
        create: "{{user}} created {{actionDescription}}",
        update: "{{user}} updated {{actionDescription}}",
        delete: "{{user}} deleted {{actionDescription}}",
        send: "{{user}} sent {{actionDescription}}"
      },
      time: {
        now: "Just now",
        minutesAgo: "{{count}} minute{{plural}} ago",
        hoursAgo: "{{count}} hour{{plural}} ago",
        daysAgo: "{{count}} day{{plural}} ago"
      },
      pagination: {
        info: "Page {{current}} of {{total}} • {{showing}} of {{total_activities}} activities",
        previous: "Previous page",
        next: "Next page"
      }
    },

    // Notes modal in dashboard
    notes: {
      modal: {
        createTitle: "Create new note",
        editTitle: "Edit note",
        titleLabel: "Note title *",
        titlePlaceholder: "Enter note title",
        contentLabel: "Content *",
        contentPlaceholder: "Describe your note in detail...",
        tagsLabel: "Tags",
        tagsPlaceholder: "Type and press Enter to add tags",
        tagsHelp: "Use tags to organize your notes",
        charactersLabel: "characters",
        cancelButton: "Cancel",
        createButton: "Create note",
        updateButton: "Update note",
        savingLabel: "Saving...",
        errors: {
          titleRequired: "Title is required",
          titleTooLong: "Title cannot exceed 255 characters",
          contentRequired: "Content is required",
          contentTooLong: "Content cannot exceed 5000 characters",
          tagsTooLong: "Tags cannot exceed 500 characters",
          saveError: "Error saving note",
          saveErrorGeneral: "An error occurred while saving"
        },
        messages: {
          createSuccess: "Note created successfully",
          updateSuccess: "Note updated successfully"
        }
      }
    },

    // Analyzer section
    analyzer: {
      title: "AI Comparator",
      subtitle: "Analyze and rank your CVs against job offers using artificial intelligence",
      noOffers: "No job offers available",
      noCvs: "No CVs available",
      createOfferFirst: "Create a job offer first",
      uploadCvsFirst: "Upload CVs first",
      modes: {
        title: "Analysis mode",
        manual: {
          title: "Manual Mode",
          description: "Unpublished offers + manually uploaded CVs"
        },
        submissions: {
          title: "Applications Mode", 
          description: "Published offers + CVs received via applications"
        }
      },
      selection: {
        selectOffer: "Select an offer",
        noOfferAvailable: "No offer available",
        manualCvs: "Manual CVs ({{count}} selected)",
        receivedCvs: "CVs received for \"{{title}}\" ({{count}} selected)",
        selectOfferFirst: "Select an offer to see received CVs",
        analysisStatus: "• {{analyzed}} analyzed, {{unanalyzed}} not analyzed",
        selectAll: "Select all",
        deselectAll: "Deselect all",
        searchPlaceholder: "Search CVs...",
        analysisStatusFilter: "Analysis status:",
        filterAll: "All CVs",
        filterUnanalyzed: "Not analyzed",
        filterAnalyzed: "Already analyzed",
        noCvsAvailable: "No manual CVs available",
        noCvsReceived: "No CVs received for this offer",
        selectOfferToSeeCvs: "Select a published offer to see received CVs"
      },
      cvLabels: {
        manual: "Manual",
        application: "Application",
        textExtracted: "Text extracted",
        textRequired: "Text required",
        alreadyAnalyzed: "Already analyzed",
        published: "Published"
      },
      actions: {
        export: "Export (CSV)",
        startAnalysis: "Launch AI analysis",
        analyzing: "Analysis in progress...",
        startAnalysisButton: "Start analysis"
      },
      analysisInfo: {
        limitTitle: "AI analysis limit",
        limitDescription: "You can analyze a maximum of 25 CVs at a time to ensure optimal quality analysis. To analyze other CVs from the same offer, perform several successive analyses.",
        startTitle: "Launch AI analysis",
        startDescription: "Analyze {{count}}/25 CV(s) for offer \"{{title}}\"",
        startDescriptionEmpty: "Select an offer and CVs to begin"
      },
      results: {
        title: "Analysis results (Ranked by score)",
        viewDetails: "View details",
        exportResults: "Export results",
        noResults: "No analysis results",
        detailModalTitle: "Detailed analysis - {{name}}",
        globalScore: "Global score",
        recommendation: "Recommendation",
        rank: "Rank: #{{rank}}",
        detailedAnalysis: "Detailed analysis",
        strengths: "Strengths", 
        weaknesses: "Areas for improvement",
        skillsEvaluation: "Skills evaluation",
        skillsRadarTitle: "Comparative skills analysis",
        close: "Close"
      },
      stats: {
        analyzed: "CVs Analyzed",
        recommended: "Recommended",
        consider: "To Consider",
        avgScore: "Average Score"
      },
      analysis: {
        inProgress: "Analysis in progress...",
        completed: "Analysis completed",
        progress: "Progress",
        estimatedTime: "Estimated time",
        analyzing: "Analyzing",
        of: "of",
        cvs: "CVs",
        cancel: "Cancel analysis"
      },
      reanalysisModal: {
        title: "Confirm re-analysis",
        confirm: "Yes, redo analysis",
        cancel: "Cancel"
      },
      errors: {
        dataLoadFailed: "Error loading data",
        cvsLoadFailed: "Error loading CVs",
        selectOfferAndCvs: "Please select an offer and at least one CV",
        maxCvsAtOnce: "You can only analyze a maximum of 25 CVs at once",
        reanalysisError: "Error during re-analysis",
        analysisError: "Error during analysis",
        exportError: "Error during export",
        noOfferSelected: "Please select a job offer",
        noCvsSelected: "Please select at least one CV",
        analysisLimitReached: "AI analysis limit reached for this month",
        upgradeRequired: "Upgrade required"
      },
      warnings: {
        maxCvsLimit: "You can select a maximum of 25 CVs for analysis"
      },
      messages: {
        limitedSelection: "Selection limited to the first 25 CVs ({{count}} CVs available)",
        analysisComplete: "Analysis completed! {{count}} CVs analyzed",
        analysisCancelled: "Analysis cancelled",
        exportSuccess: "Results exported successfully",
        analysisStarted: "AI analysis started"
      }
    },

    // Results section
    results: {
      title: "Analysis Results",
      subtitle: "View and export your AI analysis results",
      noResults: "No analysis results",
      runFirstAnalysis: "Perform your first AI analysis to see results",
      filters: {
        all: "All results",
        recent: "Recent",
        byOffer: "By offer",
        byScore: "By score",
        searchPlaceholder: "Search in results..."
      },
      sorting: {
        newest: "Newest",
        oldest: "Oldest",
        highestScore: "Highest score",
        lowestScore: "Lowest score",
        alphabetical: "Alphabetical"
      },
      actions: {
        view: "View details",
        export: "Export",
        delete: "Delete",
        compareAll: "Compare all",
        sendToKanban: "Send to Kanban",
        generateReport: "Generate report"
      },
      export: {
        title: "Export results",
        format: "Export format",
        excel: "Excel (.xlsx)",
        csv: "CSV (.csv)",
        pdf: "PDF (.pdf)",
        includeDetails: "Include analysis details",
        includeScores: "Include scores",
        includeRecommendations: "Include recommendations",
        exporting: "Exporting...",
        success: "Export successful",
        error: "Export error"
      },
      scoreRanges: {
        excellent: "Excellent (90-100%)",
        good: "Good (70-89%)",
        average: "Average (50-69%)",
        poor: "Poor (0-49%)"
      },
      details: {
        title: "Analysis details",
        candidate: "Candidate",
        offer: "Job offer",
        overallScore: "Overall score",
        analysisDate: "Analysis date",
        skillsMatch: "Skills match",
        experienceMatch: "Experience match",
        educationMatch: "Education match",
        strengths: "Strengths",
        weaknesses: "Areas for improvement",
        recommendation: "Recommendation",
        aiAnalysis: "Detailed AI analysis"
      },
      comparison: {
        title: "Candidate comparison",
        selectCandidates: "Select candidates to compare",
        compare: "Compare",
        criteria: "Comparison criteria",
        skills: "Skills",
        experience: "Experience",
        education: "Education",
        overall: "Overall score"
      },
      messages: {
        exported: "Results exported successfully",
        deleted: "Result deleted successfully",
        sentToKanban: "Candidate sent to Kanban"
      }
    },

    // Kanban page
    kanban: {
      title: "Kanban Board",
      subtitle: "Manage your candidates with an interactive visual board",
      actions: {
        newColumn: "New column",
        column: "Column",
        addCandidate: "Add candidate",
        editColumn: "Edit column",
        deleteColumn: "Delete column"
      },
      stats: {
        totalCandidates: "Total candidates",
        activeColumns: "Active columns",
        visibleCandidates: "Visible candidates"
      },
      exitFullscreen: "Exit fullscreen",
      enterFullscreen: "Fullscreen",
      searchPlaceholder: "Search for a candidate...",
      filters: {
        allScores: "All scores",
        above70: "Score ≥ 70",
        above80: "Score ≥ 80",
        above90: "Score ≥ 90"
      },
      candidates: "candidates",
      filtered: "filtered",
      noCandidates: "No candidates",
      noMatchingCandidates: "No matching candidates",
      tryAdjustingFilters: "Try adjusting your filters",
      candidate: "Candidate",
      analyzed: "Analyzed",
      columns: {
        received: "Received",
        screening: "Screening",
        interview: "Interview",
        selected: "Selected",
        hired: "Hired"
      },
      candidateActions: {
        addNote: "Add note",
        viewProfile: "View profile",
        downloadCV: "Download CV",
        moveToColumn: "Move to",
        delete: "Delete"
      },
      noItems: "No candidates in this column",
      dragHint: "Drag and drop candidates between columns",
      errors: {
        loadingBoard: "Error loading",
        loadingBoardToast: "Error loading board",
        movingCandidate: "Error moving",
        movingCandidateToast: "Error moving candidate",
        removingCandidate: "Error removing",
        removingCandidateToast: "Error removing candidate",
        deletingCandidateToast: "Error deleting candidate",
        deletingColumn: "Error deleting",
        deletingColumnToast: "Error deleting column",
        columnHasCandidates: "This column contains {{count}} candidate(s). Please move them before deleting the column."
      },
      messages: {
        candidateMoved: "Candidate moved successfully",
        candidateRemoved: "Candidate removed from kanban successfully",
        columnDeleted: "Column deleted successfully",
        candidateDeleted: "Candidate deleted successfully"
      },
      tooltips: {
        addCandidate: "Add candidate",
        editColumn: "Edit column",
        deleteColumn: "Delete column"
      },
      confirmations: {
        removeCandidate: {
          title: "Remove candidate",
          message: "Are you sure you want to remove this candidate from kanban?\n(The candidate profile will be kept in CVs)",
          confirm: "Remove",
          cancel: "Cancel"
        },
        deleteCandidate: {
          title: "Delete candidate",
          message: "Are you sure you want to permanently delete this candidate?",
          confirm: "Delete",
          cancel: "Cancel"
        },
        deleteColumn: {
          title: "Delete column",
          message: "Are you sure you want to delete this column? This action is irreversible.",
          confirm: "Delete",
          cancel: "Cancel"
        }
      },
      columnModal: {
        createTitle: "Create column",
        editTitle: "Edit column",
        nameLabel: "Column name *",
        namePlaceholder: "Ex: Pending, In progress, Completed...",
        colorLabel: "Color",
        previewLabel: "Preview",
        previewNamePlaceholder: "Column name",
        previewDescription: "Candidates will be displayed here",
        cancel: "Cancel",
        create: "Create",
        update: "Update",
        saving: "Saving...",
        saveError: "Error saving",
        createSuccess: "Column created successfully",
        updateSuccess: "Column updated successfully"
      }
    },



    // HR Management page
    hrManagement: {
      title: "HR Management",
      subtitle: "Manage your team's HR user accounts",
      noUsers: "No users found",
      createFirst: "Start by adding an HR user",
      searchPlaceholder: "Search for a user...",
      addUser: "Add HR user",
      
      // Dashboard stats
      stats: {
        hrUsers: "HR Users",
        totalAnalyses: "Total Analyses", 
        createdOffers: "Created Offers",
        uploadedCvs: "Uploaded CVs"
      },
      
      // License information
      license: {
        title: "License Information",
        currentPackage: "Current Package",
        status: "Status",
        expiration: "Expiration",
        active: "Active",
        inactive: "Inactive",
        notDefined: "Not defined",
        notDefinedDate: "Not defined"
      },
      
      // Filters
      filters: {
        allStatuses: "All statuses",
        verified: "Verified",
        unverified: "Unverified"
      },
      
      // Table headers
      table: {
        user: "User",
        email: "Email",
        status: "Status",
        createdDate: "Creation Date",
        actions: "Actions"
      },
      
      // User status
      userStatus: {
        verified: "Verified",
        unverified: "Unverified",
        hrUser: "HR User",
        nameNotDefined: "Name not defined"
      },
      
      // Pagination
      pagination: {
        previous: "Previous",
        next: "Next",
        showing: "Showing",
        to: "to",
        of: "of",
        results: "results"
      },
      
      // Search and filters help text
      searchHelp: {
        modifySearch: "Try modifying your search criteria"
      },
      
      // Modals
      modals: {
        add: {
          title: "Add HR User",
          fullName: "Full name",
          emailAddress: "Email address",
          password: "Password",
          namePlaceholder: "User name",
          emailPlaceholder: "email@example.com",
          passwordPlaceholder: "Password (min. 8 characters)",
          verificationNote: "A verification email will be sent to the user",
          cancel: "Cancel",
          create: "Create user",
          creating: "Creating..."
        },
        edit: {
          title: "Edit user",
          fullName: "Full name",
          emailAddress: "Email address",
          newPassword: "New password (optional)",
          emailNote: "Changing email will reset verification",
          passwordPlaceholder: "Leave empty to not modify",
          cancel: "Cancel",
          update: "Update",
          updating: "Updating..."
        },
        delete: {
          title: "Delete user",
          confirmText: "Are you sure you want to delete the user",
          warningText: "This action is irreversible. The user will no longer be able to access their account.",
          cancel: "Cancel",
          delete: "Delete",
          deleting: "Deleting..."
        }
      },
      
      // Action tooltips
      tooltips: {
        resendVerification: "Resend verification email",
        edit: "Edit",
        delete: "Delete"
      },
      
      // Toast messages
      messages: {
        loadError: "Error loading data",
        fillAllFields: "Please fill all fields",
        fillNameEmail: "Please fill name and email",
        passwordMinLength: "Password must contain at least 8 characters",
        userCreated: "HR user created successfully",
        createError: "Error creating user",
        userUpdated: "User updated successfully",
        updateError: "Error updating",
        userDeleted: "User deleted successfully",
        deleteError: "Error deleting",
        alreadyVerified: "User is already verified",
        verificationSent: "Verification email sent to",
        emailError: "Error sending email"
      }
    },

    // Subscription page
    subscription: {
      title: "My Subscription",
      subtitle: "Manage your subscription and view your usage",
      currentPlan: "Current Plan",
      usage: "Usage",
      billing: "Billing",
      upgrade: "Upgrade",
      downgrade: "Downgrade",
      cancel: "Cancel subscription",
      renew: "Renew",
      changePaymentMethod: "Change payment method",
      billingHistory: "Billing history",
      nextBilling: "Next billing",
      autoRenewal: "Auto renewal",
      
      // Subscription details
      subscriptionDetails: "Subscription Details",
      startDate: "Start date",
      endDate: "Access end",
      autoRenewalActive: "⚡ Auto renewal active",
      changePlan: "Change plan",
      exploreOtherPlans: "Explore our other plans to get more features",
      perMonth: "/month",
      
      // Status
      status: {
        active: "Active",
        cancelled: "Cancelled",
        expired: "Expired"
      },
      
      // Auto renewal section
      autoRenewalTitle: "Auto Renewal Active",
      autoRenewalDescription: "Your subscription will automatically renew on {{date}} for {{price}}. Usage counters will be reset on this date.",
      
      // Cancelled renewal section
      cancelledRenewalTitle: "Auto Renewal Cancelled",
      cancelledRenewalDescription: "Your subscription will not renew automatically. You keep access until {{date}}. You can reactivate auto renewal at any time.",
      
      // Actions
      cancelAutoRenewal: "Cancel auto renewal",
      activateAutoRenewal: "Activate auto renewal",
      
      // Usage section
      refreshUsageData: "Refresh usage data",
      used: "used",
      limitAlmostReached: "Limit almost reached",
      noDataAvailable: "No data available",
      usageStatsWillAppear: "Usage statistics will appear here",
      
      // Plan change options
      otherPlansAvailable: "Other Available Plans",
      discoverPlansEvolution: "Discover our plans to evolve according to your needs",
      upgradeAction: "⬆️ UPGRADE",
      economical: "⬇️ ECONOMICAL",
      
      // Plan names
      planNames: {
        demo: "Demo Free",
        essential: "Essential HR",
        enterprise: "Enterprise"
      },
      
      // Plan descriptions
      planDescriptions: {
        demo: "Discover our solution",
        essential: "For growing HR teams",
        enterprise: "Complete enterprise solution"
      },
      
      // Features
      unlimitedAnalyses: "Unlimited analyses",
      analysesPerMonth: "analyses/month",
      hrUser: "HR user",
      hrUsers: "HR users",
      jobOffers: "job offers",
      cvsPerMonth: "CVs/month",
      cvUploads: "CV uploads",
      aiAnalysesPerMonth: "AI analyses/month",
      aiResumesPerMonth: "AI summaries/month",
      appointments: "Appointments",
      customMinioStorage: "Custom MinIO Storage",
      videoAutomation: "Meeting URL Automation",
      
      // Plan change actions
      upgradeToNext: "Upgrade to next level",
      choosePlan: "Choose this plan",
      selectPlan: "Select",
      validating: "Validating...",
      
      // Plan change information
      planChangeTerms: "Plan change terms",
      upgradesInfo: "<strong>Upgrades:</strong> Applied immediately with prorated calculation",
      downgradesInfo: "<strong>Downgrades:</strong> Take effect at the end of current period",
      countersInfo: "<strong>Counters:</strong> Reset during plan changes",
      
      // Usage statistics labels
      usageLabels: {
        used: "used",
        allowed: "allowed",
        excess: "in excess"
      },
      
      // Error messages
      errors: {
        loadingSubscription: "Error loading subscription",
        planChangeValidation: "Error validating plan change",
        cancellation: "Error during cancellation",
        reactivation: "Error during reactivation",
        downgradeImpossible: "Downgrade impossible",
        currentUsageExceeds: "Your current usage exceeds the limits of the selected plan:\n\n{{conflicts}}\n\nPlease first remove excess items before proceeding with the downgrade.",
        understood: "Understood"
      },
      
      // Success messages
      success: {
        autoRenewalCancelled: "Auto renewal cancelled successfully",
        autoRenewalActivated: "Auto renewal activated successfully"
      },
      
      // Confirmation modal
      confirmModal: {
        cancelAutoRenewalTitle: "Cancel auto renewal",
        cancelAutoRenewalMessage: "Are you sure you want to cancel auto renewal of your subscription?\n\n• No future automatic billing\n• Access maintained until {{date}}\n• Possibility to reactivate renewal at any time",
        cancelRenewal: "Cancel renewal",
        keepRenewal: "Keep renewal"
      },
      
      // Currency display
      pricesShownIn: "Prices shown in"
    },

    // Applications page
    applications: {
      title: "Applications",
      subtitle: "Manage applications received via your published offers",
      noApplications: "No applications received",
      
      // Published offers section
      publishedOffers: {
        title: "Offers and applications",
        noOffers: "No published offers or offers with applications",
        noOffersHint: "Publish an offer to receive applications",
        copyUrl: "Copy URL",
        urlCopied: "URL copied to clipboard",
        applicationCount: "application",
        applicationCountPlural: "applications",
        viewCount: "view",
        viewCountPlural: "views",
        newApplications: "new",
        newApplicationsPlural: "new",
        shortlistedApplications: "shortlisted",
        shortlistedApplicationsPlural: "shortlisted",
        unpublished: "Unpublished"
      },
      
      // Applications list section
      applicationsReceived: "Applications received",
      selectOffer: "Select an offer",
      selectOfferPrompt: "Select a published offer to see applications",
      noApplicationsForOffer: "No applications for this offer",
      noApplicationsHint: "New applications will appear here",
      
      // Filters and search
      filters: {
        all: "All",
        allStatuses: "All statuses",
        new: "New",
        reviewed: "Reviewed",
        shortlisted: "Shortlisted",
        rejected: "Rejected",
        searchPlaceholder: "Search..."
      },
      
      // Status badges
      status: {
        new: "New",
        reviewed: "Reviewed",
        shortlisted: "Shortlisted",
        rejected: "Rejected"
      },
      
      // Actions
      actions: {
        viewDetails: "View details",
        previewCV: "CV preview",
        delete: "Delete application",
        sendAppointment: "Send appointment invitation",
        markAsReviewed: "Mark as reviewed",
        shortlist: "Shortlist",
        reject: "Reject",
        manageCV: "Manage CV",
        addToKanban: "Add to Kanban",
        alreadyInKanban: "Already in Kanban"
      },
      
      // Application modal
      modal: {
        candidateInfo: "Candidate information",
        currentStatus: "Current status",
        candidateMessage: "Candidate message",
        internalNotes: "Internal notes",
        addNote: "Add internal note about this candidate...",
        addNoteButton: "Add note",
        noNotes: "No internal notes for this application",
        noNotesHint: "Add the first note to document your exchanges",
        modified: "(modified)",
        edit: "Edit",
        save: "Save",
        loadMoreNotes: "See more notes ({{count}} remaining)",
        appliedOn: "Application submitted on {{date}}",
        source: "Source: {{source}}"
      },
      
      // CV Preview modal
      cvPreview: {
        title: "CV Preview",
        docxFile: "DOCX File",
        docxNotSupported: "Direct preview of DOCX files is not yet available.",
        extractedContent: "Extracted content:",
        textNotExtracted: "Text has not yet been extracted from this file.",
        downloadFile: "Download file",
        oldDocFormat: "DOC File (old format)",
        oldDocNotSupported: "Preview of .DOC files is not supported.\nPlease download the file to view it.",
        unsupportedFormat: "Unsupported format",
        unsupportedMessage: "This file type cannot be previewed.",
        browserNotSupported: "Your browser does not support PDF display.",
        clickToDownload: "Click here to download the file.",
        fileLabel: "File:",
        sizeLabel: "Size:",
        download: "Download",
        loadError: "Error loading PDF"
      },
      
      // Pagination
      pagination: {
        showing: "Showing {{start}} to {{end}} of {{total}} applications"
      },
      
      // Confirmation dialogs
      confirmations: {
        deleteTitle: "Delete application",
        deleteMessage: "Are you sure you want to delete {{name}}'s application?\n\nThis action is irreversible and will also delete the associated CV.",
        deleteButton: "Delete",
        cancelButton: "Cancel"
      },
      
      // Delete application modal
      deleteApplicationModal: {
        title: "Delete Application",
        message: "You are about to delete {{name}}'s application.",
        activeAppointmentsNotice: "This candidate has active or pending appointments. The CV will automatically be kept in the Upload section.",
        keepCvOption: "Delete application only",
        keepCvDescription: "The CV will be kept and remain accessible in the Upload section",
        deleteAllOption: "Delete application and CV",
        deleteAllDescription: "Permanently delete the application and associated CV",
        confirm: "Confirm"
      },
      
      // Messages and errors
      messages: {
        dataLoadError: "Error loading data",
        applicationLoadError: "Error loading application",
        statusUpdated: "Status updated successfully",
        statusUpdateError: "Error updating status",
        applicationDeleted: "Application deleted successfully",
        applicationDeletedCvKept: "Application deleted successfully. The CV has been kept in the Upload section",
        deleteError: "Error deleting",
        noCVAssociated: "No CV associated with this application",
        cvLoadError: "Error loading CV",
        noteLoadError: "Error loading notes",
        noteAdded: "Note added successfully",
        noteAddError: "Error adding note",
        noteUpdated: "Note updated successfully",
        noteUpdateError: "Error updating note",
        noteDeleted: "Note deleted successfully",
        noteDeleteError: "Error deleting note",
        addedToKanban: "Candidate added to Kanban successfully",
        alreadyInKanban: "Candidate is already in Kanban",
        addToKanbanError: "Failed to add candidate to Kanban",
        aiConfigMissing: "AI configuration is not set. Add your API keys in Settings > Developer > AI Providers.",
        aiInvalidToken: "Invalid AI API key. Please verify your token in Settings > Developer > AI Providers."
      },
      
      // Legacy compatibility
      candidate: {
        name: "Name",
        email: "Email",
        phone: "Phone",
        appliedDate: "Application date",
        position: "Position"
      }
    }
  },

  // Notes page
  notes: {
    title: "Notes Management",
    subtitle: "Create, organize and share your notes with your team",
    actions: {
      createNote: "New note",
      editNote: "Edit note",
      deleteNote: "Delete note",
      shareNote: "Share note"
    },
    createNote: "Create note",
    editNote: "Edit note",
    deleteNote: "Delete note",
    noteTitle: "Note title",
    noteContent: "Content",
    candidate: "Candidate",
    offer: "Job offer",
    tags: "Tags",
    priority: "Priority",
    high: "High",
    medium: "Medium",
    low: "Low",
    stats: {
      myNotes: "My notes",
      sharedNotes: "Shared notes",
      total: "Total"
    },
    search: {
      placeholder: "Search in notes..."
    },
    filters: {
      all: "All notes",
      own: "My notes",
      shared: "Shared notes",
      byCandidate: "By candidate",
      byOffer: "By offer",
      byPriority: "By priority"
    },
    results: {
      count: "{{count}} note found",
      countPlural: "{{count}} notes found",
      found: "found",
      foundPlural: "found"
    },
    empty: {
      noNotesFound: "No notes found",
      noNotesYet: "No notes yet",
      tryModifyingSearch: "Try modifying your search criteria",
      createFirstNote: "Create your first note to get started",
      createNote: "Create note"
    },
    card: {
      shared: "Shared",
      sharedBy: "by {{name}}",
      tooltips: {
        share: "Share",
        edit: "Edit",
        delete: "Delete"
      }
    },
    messages: {
      loadError: "Error loading notes",
      deleteSuccess: "Note deleted successfully",
      deleteError: "Error deleting",
      deleteErrorGeneral: "Error deleting note",
      shareSuccess: "Note shared successfully",
      shareError: "Error sharing note",
      unshareSuccess: "Note sharing stopped",
      unshareError: "Error stopping sharing",
      selectUsers: "Select at least one user"
    },
    delete: {
      title: "Delete note",
      message: "Are you sure you want to delete this note?\n\nThis action is irreversible.",
      confirmText: "Delete",
      cancelText: "Cancel"
    },
    modal: {
      createTitle: "Create new note",
      editTitle: "Edit note",
      titleLabel: "Note title *",
      titlePlaceholder: "Enter note title",
      contentLabel: "Content *",
      contentPlaceholder: "Describe your note in detail...",
      tagsLabel: "Tags",
      tagsPlaceholder: "Type and press Enter to add tags",
      tagsHelp: "Use tags to organize your notes",
      charactersLabel: "characters",
      cancelButton: "Cancel",
      createButton: "Create note",
      updateButton: "Update note",
      savingLabel: "Saving...",
      errors: {
        titleRequired: "Title is required",
        titleTooLong: "Title cannot exceed 200 characters",
        contentRequired: "Content is required",
        contentTooLong: "Content cannot exceed 5000 characters",
        tagsTooLong: "Tags cannot exceed 500 characters",
        saveError: "Error saving note",
        saveErrorGeneral: "An error occurred while saving"
      },
      messages: {
        createSuccess: "Note created successfully",
        updateSuccess: "Note updated successfully"
      }
    },
    shareModal: {
      title: "Share note",
      searchPlaceholder: "Search for a team member...",
      currentlyShared: "Currently shared with:",
      teamMembers: "Team members",
      noMembersFound: "No members found with this search",
      noMembers: "No members in your team",
      selected: "selected",
      selectedPlural: "selected",
      share: "Share",
      stopSharing: "Stop sharing",
      cancel: "Cancel",
      sharing: "Sharing...",
      shared: "Shared",
      stopSharingConfirm: {
        title: "Stop sharing",
        message: "Are you sure you want to stop sharing this note? All users will lose access.",
        confirmText: "Stop sharing",
        cancelText: "Cancel"
      }
    },
    noNotes: "No notes created",
    createFirst: "Create your first note"
  },

  // Offers section
  offers: {
    title: "Offers Management",
    subtitle: {
      admin: "Manage your team's offers",
      hr: "Offers shared with your team"
    },
    noOffers: "No job offers created",
    createFirst: "Create your first job offer to get started",
    actions: {
      createNew: "New offer",
      create: "Create offer",
      menu: "Actions menu",
      view: "View details",
      edit: "Edit",
      delete: "Delete",
      duplicate: "Duplicate",
      archive: "Archive",
      publish: "Publish offer",
      unpublish: "Unpublish",
      copyUrl: "Copy URL"
    },
    status: {
      open: "Open",
      closed: "Closed",
      cancelled: "Cancelled",
      draft: "Draft",
      archived: "Archived"
    },
    fields: {
      title: "Job title",
      client: "Client",
      location: "Location",
      type: "Contract type",
      experience: "Required experience",
      skills: "Required skills",
      salary: "Salary",
      description: "Description",
      requirements: "Requirements",
      benefits: "Benefits",
      deadline: "Deadline",
      createdAt: "Created on",
      updatedAt: "Updated on"
    },
    labels: {
      published: "Published",
      team: "Team",
      requiredSkills: "Required skills",
      createdOn: "Created on",
      modifiedOn: "Modified on",
      publishedOn: "Published on",
      view: "view"
    },
    stats: {
      total: "Total",
      open: "Open",
      closed: "Closed",
      cancelled: "Cancelled"
    },
    search: {
      placeholder: "Search by title, client, or skills..."
    },
    filters: {
      title: "Filters",
      search: "Search",
      status: "Status",
      active: "Active filters",
      clearAll: "Reset all",
      allStatuses: "All statuses",
      open: "Open",
      closed: "Closed",
      cancelled: "Cancelled"
    },
    emptyState: {
      title: "No offers found",
      filterHint: "Try modifying your search filters.",
      createHint: "Start by creating your first job offer."
    },
    deleteModal: {
      title: "Delete offer",
      message: "Are you sure you want to delete this offer?\n\nThis action will also delete all associated analysis results.",
      confirm: "Delete",
      cancel: "Cancel"
    },
    unpublishModal: {
      title: "Unpublish offer",
      message: "Are you sure you want to unpublish this offer?\n\nThe public link will no longer be accessible to candidates.",
      confirm: "Unpublish",
      cancel: "Cancel"
    },
    errors: {
      loadFailed: "Error loading offers",
      deleteFailed: "Error deleting",
      hasDependencies: "This CV cannot be deleted because it is linked to other records.",
      notFound: "CV not found",
      publishFailed: "Error publishing",
      unpublishFailed: "Error unpublishing",
      createFailed: "Error creating",
      updateFailed: "Error updating",
      usageLimitReached: "Usage limit reached"
    },
    messages: {
      createSuccess: "Offer created successfully",
      updateSuccess: "Offer updated successfully",
      deleteSuccess: "Offer deleted successfully",
      publishSuccess: "Offer published successfully!",
      unpublishSuccess: "Offer unpublished",
      urlCopied: "URL copied to clipboard"
    },
    modal: {
      createTitle: "New offer",
      editTitle: "Edit offer",
      subtitle: "Fill in the information to create an attractive job offer",
      steps: {
        basic: "Basic information",
        details: "Job details",
        description: "Description"
      },
      step1: {
        title: "Basic information"
      },
      step2: {
        title: "Skills and requirements"
      },
      step3: {
        title: "Job description"
      }
    },
    form: {
      title: "Offer title",
      titlePlaceholder: "Ex: Senior React Developer",
      titleHint: "A clear and precise title attracts more candidates",
      client: "Client",
      clientPlaceholder: "Ex: TechCorp",
      location: "Location",
      locationPlaceholder: "Ex: Paris, Remote",
      duration: "Duration",
      durationPlaceholder: "Ex: 6 months, Permanent",
      status: "Status",
      statusOpen: "Open",
      statusClosed: "Closed",
      statusCancelled: "Cancelled",
      requiredSkills: "Required skills",
      skillsPlaceholder: "Type a skill and press Enter",
      skillsHint: "Type a skill and press Enter or comma to add it",
      addSkill: "Add",
      description: "Full description",
      descriptionPlaceholder: "Describe the position, responsibilities, work environment...",
      descriptionHint: "A detailed description helps candidates better understand the position",
      preview: "Offer preview",
      previous: "Previous",
      next: "Next",
      cancel: "Cancel",
      create: "Create",
      update: "Update",
      creating: "Creating...",
      updating: "Updating..."
    },
    validation: {
      titleRequired: "Title is required",
      skillsRequired: "At least one skill is required",
      descriptionRequired: "Description is required"
    }
  },

  // CVs section
  cvs: {
    title: "CVs Management",
    subtitle: {
      admin: "CVs shared with your HR team",
      hr: "CVs accessible to your entire team"
    },
    noCvs: "No CVs uploaded",
    uploadFirst: "Upload your first CV to start analysis",
    upload: {
      title: "Upload CVs",
      dragDrop: "Drag and drop your files here",
      or: "or",
      clickToSelect: "Click to select",
      orDragDrop: "or drag and drop your files here",
      pdfOnly: "PDF or DOCX files only, up to 10MB per file",
      supportedFormats: "Supported formats: PDF, DOCX",
      maxSize: "Maximum size: 10 MB per file",
      maxFiles: "Maximum 10 files at once",
      maxFilesLimit: "Maximum 50 files per upload",
      uploading: "Uploading...",
      processing: "Processing files...",
      clickToSelectFiles: "Click to select your files",
      selectedFiles: "Selected files",
      clearSelection: "Clear selection",
      startExtraction: "Upload + Extract text",
      processingExtraction: "Upload + Extracting...",
      success: "CVs uploaded successfully",
      error: "Error uploading"
    },
    sections: {
      byOffer: "CVs by Published Offer",
      manual: "Manually Uploaded CVs",
      searchResults: "Search results",
      all: "All CVs",
      applications: "Application CVs",
      applicationsUnlinked: "Application CVs (No Offer)",
      manualFull: "Manually Uploaded CVs"
    },
    tabs: {
      offers: "CV bank by published offers",
      manual: "Manually uploaded CV bank"
    },
    filters: {
      title: "Filters",
      search: "Search",
      type: "Type",
      origin: "Origin",
      offer: "Offer",
      active: "Active filters",
      clearAll: "Reset all",
      all: "All CVs",
      recent: "Recent",
      analyzed: "Analyzed",
      notAnalyzed: "Not analyzed",
      searchPlaceholder: "Search by candidate name...",
      allTypes: "All types",
      allOrigins: "All origins",
      allOffers: "All offers",
      manualUpload: "Manual upload",
      application: "Application"
    },
    actions: {
      view: "View CV",
      download: "Download",
      delete: "Delete",
      analyze: "Analyze",
      editCandidate: "View/Edit profile",
      generateProfile: "Generate candidate profile",
      addToKanban: "Add to Kanban",
      removeFromKanban: "Remove from Kanban",
      sendAppointment: "Send appointment invitation",
      upload: "Upload CVs",
      preview: "Preview CV",
      aiResume: "AI Summary + candidate profile creation"
    },
    fields: {
      candidate: "Candidate",
      filename: "File name",
      uploadDate: "Upload date",
      fileSize: "Size",
      status: "Status",
      lastAnalysis: "Last analysis",
      score: "Score"
    },
    status: {
      uploaded: "Uploaded",
      processing: "Processing",
      analyzed: "Analyzed",
      error: "Error"
    },
    candidateProfile: {
      title: "Candidate Profile",
      generateWith: "Generate with AI",
      generating: "Generating...",
      generated: "Profile generated successfully",
      summary: "Professional summary",
      experience: "Experience",
      skills: "Skills",
      education: "Education",
      languages: "Languages",
      contact: "Contact"
    },
    confirmDelete: "Are you sure you want to delete this CV?",
    labels: {
      team: "Team",
      selectedFromApplication: "CV selected from application",
      receivedViaApplication: "CV received via public application",
      receivedFromApplications: "CVs received via public applications",
      uploadedByTeam: "CVs uploaded by your team",
      offer: "Offer",
      application: "Application",
      manual: "Manual",
      candidateCardCreated: "Profile created",
      sharedToKanban: "Shared to kanban"
    },
    stats: {
      total: "Total",
      documents: "PDF/DOCX",
      manual: "Manual upload",
      applications: "Applications"
    },
    search: {
      placeholder: "Search by file name..."
    },
    viewMode: {
      label: "Display mode",
      sections: "Sections",
      grouped: "By Offer",
      list: "List",
      expandAll: "Expand all",
      collapseAll: "Collapse all"
    },
    emptyState: {
      title: "No CVs found",
      filterHint: "Try modifying your search filters.",
      uploadHint: "Start by uploading your first CVs."
    },
    deleteModal: {
      title: "Delete CV",
      message: "Are you sure you want to delete CV \"{{filename}}\"?\n\nThis action is irreversible.",
      confirm: "Delete",
      cancel: "Cancel",
      warnings: {
        hasKanbanCard: "⚠️ This candidate profile is shared in the kanban. Deleting the CV will also remove the candidate profile and stop tracking in the kanban.",
        hasAppointments: "⚠️ The candidate for this CV has active appointments. Are you sure you want to delete their CV?"
      }
    },
    candidateDeleteModal: {
      title: "Delete candidate profile",
      message: "Are you sure you want to delete this candidate profile?\n\nAll associated data will be permanently lost.",
      confirm: "Delete profile",
      cancel: "Cancel"
    },
    errors: {
      loadFailed: "Error loading CVs",
      uploadFailed: "Error uploading",
      uploadFailedAll: "Failed to upload any files",
      deleteFailed: "Error deleting",
      downloadFailed: "Error downloading CV",
      analysisFailed: "Error analyzing",
      shareFailed: "Error sharing",
      unshareFailed: "Error removing",
      invalidFileType: "⚠️ Only PDF or DOCX files are accepted. Please convert your documents before uploading.",
      fileTooLarge: "File is too large. Maximum size is 10MB.",
      tooManyFiles: "Too many files selected. Please upload fewer files at once.",
      noValidFiles: "No valid PDF/DOCX files detected",
      noValidSelected: "No valid PDF/DOCX files selected",
      previewUnsupported: "Preview is only available for PDF files. Download the file to view it."
    },
    warnings: {
      extractTextFirst: "Please first extract text from the CV"
    },
    messages: {
      uploadSuccess: "{{count}} CV(s) uploaded successfully",
      duplicateFile: "Document \"{{filename}}\" already exists. If this is an update, please delete the old document or change its name.",
      noNewFiles: "No new CVs were added - all files already existed.",
      deleteSuccess: "CV deleted successfully",
      candidateCreated: "Candidate profile created successfully",
      candidateDeleted: "Candidate profile deleted successfully",
      sharedToKanban: "Candidate shared to kanban successfully",
      unsharedFromKanban: "Candidate removed from kanban successfully",
      uploaded: "CV uploaded successfully",
      deleted: "CV deleted successfully",
      profileGenerated: "Candidate profile generated successfully"
    }
  },

  // Security & Privacy Page
  security: {
    title: "Security and Privacy",
    subtitle: "Your data is secure",
    
    tabs: {
      dataHosting: {
        label: "Data Hosting",
        description: "Where your data is stored"
      },
      gdpr: {
        label: "GDPR and Compliance",
        description: "Regulatory compliance"
      },
      dataUsage: {
        label: "Data Usage",
        description: "How we use your data"
      },
      userControl: {
        label: "User Control",
        description: "Managing your data"
      },
      aiSecurity: {
        label: "AI and Security",
        description: "Protection during AI analysis"
      }
    },
    
    features: {
      encryption: {
        title: "End-to-end encryption",
        description: "All your data is encrypted with AES-256"
      },
      gdprCompliance: {
        title: "GDPR Compliance",
        description: "Full compliance with European regulations"
      },
      secureHosting: {
        title: "Secure Hosting",
        description: "Data hosted in the European Union"
      },
      rightToForget: {
        title: "Right to be forgotten",
        description: "Complete deletion of your data at any time"
      }
    },
    
    hero: {
      badge: "Security and Privacy",
      title: "Your data is secure",
      subtitle: "Discover how we protect your personal data and respect your privacy with the highest security standards."
    },
    
    overview: {
      title: "Enterprise-level security",
      subtitle: "Candio implements security best practices to protect your sensitive data and respect your privacy."
    },
    
    detailedInfo: {
      title: "Detailed Information",
      subtitle: "Explore in detail how we manage, protect and respect your personal data."
    },
    
    dataHosting: {
      serverLocation: {
        title: "Server location",
        description: "All your data is hosted exclusively in data centers located in the <strong>European Union</strong>, ensuring compliance with European data protection laws."
      },
      infrastructure: {
        title: "Infrastructure",
        items: [
          "ISO 27001 certified data centers",
          "Multiple geographic redundancy",
          "Daily automatic backups",
          "24/7 system monitoring"
        ]
      },
      encryption: {
        title: "Encryption",
        items: [
          "AES-256 encryption at rest",
          "TLS 1.3 for transmissions",
          "Rotating encryption keys",
          "Secure access by two-factor authentication"
        ]
      }
    },
    
    gdpr: {
      compliance: {
        title: "Full GDPR compliance",
        description: "Candio is fully compliant with the General Data Protection Regulation (GDPR) and respects all your rights as a user."
      },
      rights: {
        title: "Your GDPR rights",
        access: {
          title: "Right of access",
          description: "View all your personal data"
        },
        rectification: {
          title: "Right of rectification",
          description: "Correct your personal information"
        },
        erasure: {
          title: "Right to be forgotten",
          description: "Permanently delete your data"
        },
        portability: {
          title: "Right to data portability",
          description: "Export your data in a standard format"
        },
        objection: {
          title: "Right to object",
          description: "Object to the processing of your data"
        },
        restriction: {
          title: "Right to restriction",
          description: "Limit the processing of your data"
        }
      },
      processingTimes: {
        title: "Processing times",
        acknowledgment: "Acknowledgment of receipt",
        standard: "Standard processing",
        maximum: "Maximum legal time",
        hours24: "24h",
        days7: "7 days",
        days30: "30 days"
      }
    },
    
    dataUsage: {
      exclusive: {
        title: "Exclusive and transparent use",
        description: "Your data is <strong>never sold, shared or used for other purposes</strong> than those strictly necessary for Candio's operation."
      },
      whatWeDo: {
        title: "What we do with your data",
        analysis: {
          title: "CV analysis and comparison",
          description: "AI processing to classify and analyze applications"
        },
        management: {
          title: "Account management",
          description: "Authentication, billing and customer support"
        },
        improvement: {
          title: "Service improvement",
          description: "Anonymized statistics to optimize our algorithms"
        }
      },
      whatWeDont: {
        title: "What we NEVER do",
        dataSale: {
          title: "Data sale",
          description: "Your data is never sold to third parties"
        },
        commercialSharing: {
          title: "Commercial sharing",
          description: "No sharing with commercial partners"
        },
        advertising: {
          title: "Advertising use",
          description: "No profiling for targeted advertising"
        }
      }
    },
    
    userControl: {
      fullControl: {
        title: "Full control of your data",
        description: "You maintain complete control over your personal data with features integrated directly into your account."
      },
      features: {
        title: "Features available in your account",
        export: {
          title: "Export my data",
          description: "Download all your data in JSON format",
          location: "Available in Settings → Personal data"
        },
        delete: {
          title: "Delete my account",
          description: "Permanent deletion of all your data",
          location: "Available in Settings → Account management"
        },
        view: {
          title: "View my data",
          description: "View all stored information",
          location: "Available in Settings → Privacy"
        },
        preferences: {
          title: "Manage preferences",
          description: "Control the use of your data",
          location: "Available in Settings → Preferences"
        }
      },
      deletionProcess: {
        title: "Data deletion process",
        step1: {
          title: "Deletion request",
          description: "Via your account or by contacting us"
        },
        step2: {
          title: "Identity verification",
          description: "Secure confirmation of your identity"
        },
        step3: {
          title: "Immediate deletion",
          description: "Permanent deletion within 24h maximum"
        }
      }
    },
    
    aiSecurity: {
      protection: {
        title: "Secure and private AI",
        description: "We use advanced AI models to analyze your CVs, but your data remains <strong>private and protected</strong> at all times."
      },
      models: {
        title: "AI models used",
        openai: {
          title: "OpenAI GPT-4",
          description: "Main model for semantic analysis of CVs and comparison with job offers.",
          certification: "SOC 2 Type II certified"
        },
        local: {
          title: "Local processing",
          description: "Text extraction and pre-processing performed on our secure servers.",
          guarantee: "No storage at AI providers"
        }
      },
      protectionMeasures: {
        title: "Data protection during AI analysis",
        anonymization: {
          title: "Automatic anonymization",
          description: "Identifying personal data is masked before AI analysis"
        },
        encryption: {
          title: "Encrypted transmission",
          description: "All communications with AI services are end-to-end encrypted"
        },
        noStorage: {
          title: "No external storage",
          description: "AI providers do not retain any copies of your data"
        },
        realtime: {
          title: "Real-time processing",
          description: "Immediate analysis without temporary storage at third parties"
        }
      },
      purposes: {
        title: "Purposes of AI use",
        semantic: {
          title: "Semantic analysis",
          description: "Understanding the content of CVs and job offers"
        },
        classification: {
          title: "Classification",
          description: "Ranking candidates by relevance and suitability"
        },
        synthesis: {
          title: "Synthesis",
          description: "Generation of summaries and personalized recommendations"
        }
      }
    },
    
    contact: {
      title: "Questions about security?",
      subtitle: "Our security team is available to answer all your questions regarding the protection of your data.",
      teamButton: "Contact our security team",
      emailButton: "security@lediteur.club"
    }
  },

  // Job Application Page
  jobApplication: {
    loading: "Loading...",
    error: {
      notFound: "Offer not found",
      notFoundDescription: "This offer does not exist or is no longer available.",
      general: "Error sending",
      title: "An error occurred",
      message: "Unable to load this offer. Please try again later."
    },
    success: {
      title: "Application sent!",
      description: "Thank you for your application. We have received your CV and will contact you soon.",
      canClose: "You can close this page."
    },
    company: {
      website: "Website",
      about: "About the company"
    },
    offer: {
      views: "views",
      publishedOn: "Published on",
      jobDescription: "Job description",
      requiredSkills: "Required skills"
    },
    form: {
      title: "Apply now",
      subtitle: "Join our team in just a few clicks",
      firstName: "First name",
      lastName: "Last name",
      email: "Email",
      phone: "Phone",
      message: "Message (optional)",
      messagePlaceholder: "Why are you interested in this position?",
      cv: "CV * (PDF or DOCX - 10MB max)",
      cvPlaceholder: "Click or drag your CV here",
      cvUploadText: "Drag your CV here or click to browse",
      cvFileTypes: "PDF or DOCX (max 10MB)",
      required: "*",
      submit: "Send my application",
      submitting: "Sending...",
      privacy: "By submitting this form, you agree that your data will be processed as part of this application."
    },
    validation: {
      firstNameRequired: "First name is required",
      lastNameRequired: "Last name is required",
      emailRequired: "Email is required",
      emailInvalid: "Invalid email",
      cvRequired: "CV is required",
      cvFileTypeError: "Only PDF or DOCX files are accepted. Please convert your document before uploading.",
      cvFileSizeError: "File must not exceed 10MB"
    },
    dateFormat: {
      options: {
        day: "numeric",
        month: "long", 
        year: "numeric"
      }
    },
    steps: {
      step: "Step",
      of: "of",
      personalInfo: "Personal information",
      yourCV: "Your CV",
      motivationMessage: "Motivation message"
    },
    whyJoinUs: {
      title: "Why join us?",
      careerGrowth: "Career growth",
      careerGrowthDesc: "Growth opportunities and continuous training",
      dynamicTeam: "Dynamic team",
      dynamicTeamDesc: "Collaborative and supportive environment",
      innovativeProjects: "Innovative projects",
      innovativeProjectsDesc: "Work on cutting-edge technologies",
      competitiveBenefits: "Competitive benefits",
      competitiveBenefitsDesc: "Attractive salary and benefits"
    },
    placeholders: {
      firstName: "John",
      lastName: "Doe",
      email: "john.doe@email.com",
      phone: "+1 234 567 8900",
      motivation: "Tell us about your motivation for this position..."
    },
    buttons: {
      continue: "Continue",
      back: "Back",
      remove: "Remove",
      backToHome: "Back to home"
    },
    summary: {
      title: "Application summary",
      name: "Name",
      email: "Email",
      phone: "Phone",
      cv: "CV"
    },
    expired: {
      title: "Offer expired",
      message: "This job offer is no longer available or has been withdrawn by the employer."
    }
  },

  // Messages and Notifications
  messages: {
    success: {
      saved: "Settings saved successfully",
      updated: "Updated successfully",
      deleted: "Deleted successfully",
      uploaded: "File uploaded successfully",
      sent: "Message sent successfully"
    },
    errors: {
      general: "An error occurred. Please try again.",
      network: "Network error. Please check your connection.",
      unauthorized: "You are not authorized to perform this action.",
      notFound: "The requested resource was not found.",
      serverError: "Server error. Please try again later."
    },
    confirmations: {
      delete: "Are you sure you want to delete this item?",
      unsavedChanges: "You have unsaved changes. Are you sure you want to leave?",
      logout: "Are you sure you want to log out?"
    }
  },

  // Email Templates
  emails: {
    verification: {
      subject: "Verify your Candio account",
      title: "Welcome to Candio!",
      subtitle: "Your account is almost ready, just one step left",
      description: "AI CV matching in one click",
      thankYou: "Thank you for signing up to Candio",
      instruction: "To activate your account and start using our services, please click the button below:",
      buttonText: "Verify my account",
      warning: "Important: This link expires in 24 hours for your security.",
      alternativeText: "If the button doesn't work:",
      noAccount: "If you didn't create this account, you can safely ignore this email.",
      copyright: "© 2025 Candio - All rights reserved"
    },
    contact: {
      subjectPrefix: "[Candio Contact]",
      newMessage: "New Contact Message",
      details: "Contact Details",
      name: "Name:",
      email: "Email:",
      subject: "Subject:",
      message: "Message:",
      replyText: "To reply, simply click 'Reply' in your email client.",
      autoText: "Email sent automatically from Candio"
    },
    application: {
      subject: "New application for {{offerTitle}}",
      title: "New application received",
      offerLabel: "Offer",
      candidateLabel: "Candidate",
      emailLabel: "Email",
      phoneLabel: "Phone", 
      messageLabel: "Message",
      noMessage: "No specific message",
      viewAllApplicationsButton: "📋 View all applications",
      dashboardFooter: "Log in to your dashboard to see more details and manage this application."
    },
    appointments: {
      invitation: {
        subject: "Interview Invitation - {{offerTitle}}",
        title: "Interview Invitation",
        greeting: "Hello {{candidate_name}}",
        message: "We are pleased to invite you for an interview following your application for the position of \"{{offerTitle}}\".\n\nWe would like to discuss your background and motivations for this position with you. This interview will also allow us to present our company and answer your questions.\n\nYou can choose a time slot that suits you by clicking the button below.",
        bookAppointment: "Book an appointment",
        meetingType: "Meeting type",
        online: "Online",
        physical: "In person",
        videoLinkNote: "The video conference link will be sent to you after confirming your appointment.",
        instructions: "Click the button above to access our calendar and choose a time slot that suits you.",
        phone: "Phone",
        email: "Email",
        allRightsReserved: "All rights reserved.",
        website: "Website",
        contact: "Contact",
        emailTitle: "Notification",
        dateLocale: "en-US"
      },
      confirmation: {
        subject: "Appointment Confirmation",
        greeting: "Hello {{prenom}}",
        message: "We are pleased to confirm your appointment.",
        date: "Date",
        time: "Time",
        with: "With",
        type: "Type",
        meetingLink: "Meeting link",
        clickToJoin: "Click here to join",
        linkWillBeSent: "The link will be sent to you soon",
        address: "Address",
        importantNote: "Important note",
        onlineInstructions: "Make sure you have a stable internet connection and a quiet place for your interview.",
        regards: "Sincerely"
      },
      cancellation: {
        subject: "Appointment Cancellation",
        greeting: "Hello {{prenom}}",
        message: "We inform you that your {{status_rendez_vous}} appointment scheduled for {{date}} at {{heure}} has been cancelled.",
        reason: "Reason",
        apology: "We apologize for any inconvenience caused.",
        reschedule: "If you wish to reschedule an appointment, please feel free to contact us."
      }
    }
  },

  // Analyzer Page
  analyzer: {
    title: "AI Comparator",
    subtitle: "Analyze and rank your CVs against job offers using artificial intelligence",
    
    errors: {
      dataLoadFailed: "Error loading data",
      cvsLoadFailed: "Error loading CVs",
      selectOfferAndCvs: "Please select an offer and at least one CV",
      maxCvsAtOnce: "You can only analyze a maximum of 25 CVs at once",
      analysisError: "Error during analysis",
      reanalysisError: "Error during re-analysis",
      exportError: "Error during export"
    },
    
    warnings: {
      maxCvsLimit: "You can select a maximum of 25 CVs for analysis"
    },
    
    messages: {
      limitedSelection: "Selection limited to first 25 CVs ({{count}} CVs available)",
      analysisComplete: "Analysis complete! {{count}} CVs analyzed",
      analysisCancelled: "Analysis cancelled",
      exportSuccess: "Results exported successfully"
    },
    
    reanalysisModal: {
      title: "Confirm re-analysis",
      confirm: "Yes, redo analysis",
      cancel: "Cancel",
      exactCombinationMessage: "This exact combination ({{count}} CVs) has already been analyzed against this offer on {{date}} at {{time}}.\n\nAre you sure you want to redo this identical analysis?",
      partialOverlapMessage: "{{analyzed}} CV(s) out of {{total}} have already been analyzed against this offer (but not in this exact combination).\n\nThis new combination will allow a comprehensive comparative classification.\n\nAre you sure you want to continue with this analysis?"
    },
    
    modes: {
      title: "Analysis mode",
      manual: {
        title: "Manual Mode",
        description: "Unpublished offers + manually uploaded CVs"
      },
      submissions: {
        title: "Applications Mode",
        description: "Published offers + CVs received via applications"
      }
    },
    
    stats: {
      analyzed: "CVs Analyzed",
      recommended: "Recommended",
      consider: "To Consider",
      avgScore: "Average Score"
    },
    
    selection: {
      selectOffer: "Select an offer",
      noOfferAvailable: "No offer available",
      manualCvs: "Manual CVs ({{count}} selected)",
      receivedCvs: "CVs received for \"{{title}}\" ({{count}} selected)",
      selectOfferFirst: "Select an offer to see received CVs",
      analysisStatus: "• {{analyzed}} analyzed, {{unanalyzed}} unanalyzed",
      deselectAll: "Deselect all",
      selectAll: "Select all",
      searchPlaceholder: "Search CVs...",
      analysisStatusFilter: "Analysis status:",
      filterAll: "All CVs",
      filterUnanalyzed: "Unanalyzed",
      filterAnalyzed: "Already analyzed",
      selectOfferToSeeCvs: "Select a published offer to see received CVs",
      noCvsAvailable: "No manual CV available",
      noCvsReceived: "No CV received for this offer"
    },
    
    cvLabels: {
      published: "Published",
      manual: "Manual",
      application: "Application",
      textExtracted: "Text extracted",
      textRequired: "Text required",
      alreadyAnalyzed: "Already analyzed"
    },
    
    analysisInfo: {
      limitTitle: "AI analysis limit",
      limitDescription: "You can analyze a maximum of 25 CVs at once to ensure optimal quality analysis. To analyze other CVs for the same offer, perform multiple successive analyses.",
      startTitle: "Start AI analysis",
      startDescription: "Analyze {{count}}/25 CV(s) for offer \"{{title}}\"",
      startDescriptionEmpty: "Select an offer and CVs to start"
    },
    
    actions: {
      export: "Export (CSV)",
      analyzing: "Analysis in progress...",
      startAnalysisButton: "Start analysis"
    },
    
    results: {
      title: "Analysis results (Ranked by score)",
      viewDetails: "View details",
      detailModalTitle: "Detailed analysis - {{name}}",
      globalScore: "Global score",
      recommendation: "Recommendation",
      rank: "Rank: #{{rank}}",
      detailedAnalysis: "Detailed analysis",
      strengths: "Strengths",
      weaknesses: "Areas for improvement",
      close: "Close"
    }
  },

  // Results Page
  results: {
    title: "Analysis Results",
    
    errors: {
      dataLoadFailed: "Error loading data",
      resultsLoadFailed: "Error loading results",
      deleteFailed: "Error during deletion",
      exportFailed: "Error during export"
    },
    
    messages: {
      noResults: "No analysis results found",
      deleteSuccess: "Result deleted successfully",
      exportSuccess: "Results exported successfully",
      exportAllNotAvailable: "Export for all offers not available at the moment"
    },
    
    labels: {
      cvs: "CVs",
      topScore: "Top Score",
      recommended: "Recommended",
      unknownCandidate: "Unknown candidate",
      deletedCv: "Deleted CV",
      score: "Score",
      team: "Team",
      offer: "Offer"
    },
    
    subtitle: {
      adminWithResults: "{{count}} analyses for {{offerCount}} offer(s)",
      adminNoOffers: "AI analyses from your team",
      hrWithResults: "{{count}} analysis results",
      hrNoResults: "Analysis results shared with your team"
    },
    
    stats: {
      total: "Total Analyzed",
      recommended: "Recommended",
      consider: "To Consider",
      avgScore: "Average Score"
    },
    
    filters: {
      offer: "Job offer",
      allOffers: "All offers",
      search: "Search",
      searchPlaceholder: "CV name...",
      score: "Score",
      allScores: "All scores",
      excellent: "80-100 (Excellent)",
      good: "60-79 (Good)",
      average: "40-59 (Average)",
      weak: "0-39 (Weak)",
      recommendation: "Recommendation",
      allRecommendations: "All",
      recommended: "Recommended",
      consider: "To consider",
      notRecommended: "Not recommended",
      sortBy: "Sort by",
      scoreDesc: "Score descending",
      scoreAsc: "Score ascending",
      newest: "Newest",
      oldest: "Oldest"
    },
    
    viewModes: {
      byOffer: "By Offer",
      list: "List"
    },
    
    loading: {
      offers: "Loading offers...",
      results: "Loading analysis results..."
    },
    
    emptyStates: {
      noOffers: {
        title: "No job offers",
        description: "You must first create job offers before you can analyze CVs."
      },
      noFilterResults: {
        title: "No results match the filters",
        description: "Try modifying your search filters or select another offer."
      },
      noAnalysis: {
        title: "No analysis available",
        descriptionAll: "No analysis has been performed yet. Start by analyzing CVs against your offers.",
        descriptionSpecific: "No analysis has been performed for this specific offer."
      }
    },
    
    actions: {
      refresh: "Refresh",
      export: "Export (CSV)",
      exportTitle: "Export results",
      exportNotAvailable: "Export not available for all offers",
      createOffer: "Create offer",
      analyzeCvs: "Analyze CVs",
      viewAllAnalyses: "View all analyses",
      viewDetails: "View details",
      delete: "Delete"
    },
    
    deleteModal: {
      title: "Delete result",
      message: "Are you sure you want to delete this analysis result? This action is irreversible.",
      confirm: "Delete",
      cancel: "Cancel"
    },
    
    detailModal: {
      title: "Detailed analysis - {{filename}}",
      globalScore: "Global score",
      recommendation: "Recommendation",
      analyzedOn: "Analyzed on {{date}}",
      analysisDate: "Analysis date",
      detailedAnalysis: "Detailed analysis",
      strengths: "Strengths",
      weaknesses: "Areas for improvement",
      skillsEvaluation: "Skills evaluation",
      skillsRadarTitle: "Comparative skills analysis",
      close: "Close"
    },
    
    radar: {
      score: "Score",
      required: "Required",
      noData: "No data available",
      candidateScore: "Candidate score",
      jobRequirements: "Job requirements",
      weak: "Weak",
      medium: "Medium",
      strong: "Strong"
    }
  },

  // Settings Page
  settings: {
    title: "Settings",
    subtitle: "Manage your profile, preferences and account",
    
    tabs: {
      account: "Account",
      profile: "Profile",
      password: "Password",
      preferences: "Preferences",
      company: "Company",
      billing: "Payment & Billing",
      notifications: "Notifications",
      developer: "Developer",
      usage: "Usage",
      cleanup: "Automatic Cleanup"
    },
    
    errors: {
      dataLoadFailed: "Error loading data",
      fillAllFields: "Please fill all fields",
      saveError: "Error during save"
    },
    
    actions: {
      save: "Save",
      saving: "Saving...",
      test: "Test",
      testing: "Testing...",
      delete: "Delete",
      cancel: "Cancel",
      refresh: "Refresh"
    },
    
    accountInfo: {
      title: "Account information",
      registeredOn: "Registered on {{date}}",
      verified: "Verified account",
      unverified: "Unverified",
      roles: {
        admin: "Administrator",
        rh: "HR"
      }
    },
    
    profile: {
      title: "Profile information",
      fullName: "Full name",
      fullNamePlaceholder: "Your full name",
      email: "Email address",
      emailPlaceholder: "your@email.com",
      emailChangeNote: "Changing email will require new verification",
      updateSuccess: "Profile updated successfully",
      updateError: "Error updating profile",
      loadError: "Error loading profile",
      sync: {
        success: "Statistics synchronized",
        error: "Error during synchronization"
      },
      counters: {
        initSuccess: "Counters initialized",
        initError: "Error initializing counters"
      },
      usage: {
        dataUnavailable: "Usage data unavailable",
        limitReached: "Limit reached ({{used}}/{{limit}})",
        offers: "Job offers",
        cvUploads: "Uploaded CVs",
        analyses: "AI Analyses",
        aiResume: "AI CV Summary",
        aiResumeDesc: "Creating candidate profiles via AI",
        rhUsers: "HR Users"
      }
    },
    
    password: {
      title: "Change password",
      current: "Current password",
      currentPlaceholder: "Your current password",
      new: "New password",
      newPlaceholder: "Your new password",
      confirm: "Confirm new password",
      confirmPlaceholder: "Confirm your new password",
      minLengthNote: "Minimum 8 characters",
      mismatch: "New passwords do not match",
      minLength: "New password must contain at least 8 characters",
      updateSuccess: "Password changed successfully",
      updateError: "Error changing password",
      changeButton: "Change password",
      changing: "Changing..."
    },
    
    preferences: {
      title: "Preferences",
      language: "Interface language",
      cleanupDelay: "Automatic cleanup delay",
      cleanupDelayNote: "Files will be automatically deleted after this period",
      emailNotifications: "Email notifications",
      emailNotificationsNote: "Receive notifications for completed analyses",
      updateSuccess: "Preferences updated successfully",
      updateError: "Error updating preferences",
      
      // Automatic Cleanup Settings
      automaticCleanup: {
        title: "Automatic Document Cleanup",
        subtitle: "Configure automatic cleanup to optimize your storage space",
        enable: "Enable automatic cleanup",
        schedule: "Schedule",
        scheduleType: {
          label: "Schedule type",
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
          custom: "Custom date"
        },
        frequency: {
          label: "Frequency",
          everyDay: "Every day",
          everyXDays: "Every {{count}} days"
        },
        timezone: {
          label: "Time zone",
          description: "Cleanup will be executed according to this time zone"
        },
        dayOfWeek: {
          label: "Day of week",
          monday: "Monday",
          tuesday: "Tuesday",
          wednesday: "Wednesday",
          thursday: "Thursday",
          friday: "Friday",
          saturday: "Saturday",
          sunday: "Sunday"
        },
        dayOfMonth: {
          label: "Day of month",
          day: "Day {{day}}"
        },
        time: {
          label: "Execution time",
          format: "HH:MM (24h)"
        },
        customDate: {
          label: "Date and time",
          placeholder: "Select a date"
        },
        recurring: {
          label: "Recurring execution",
          yes: "Yes, repeat according to schedule",
          no: "No, run only once"
        },
        cleanupOptions: {
          title: "Cleanup options",
          daysOld: {
            label: "Delete files older than",
            suffix: "days"
          },
          fileTypes: {
            label: "File types to clean",
            cvs: "Uploaded CVs",
            results: "Analysis results",
            tempFiles: "Temporary files"
          },
          excludeStarred: "Exclude starred items",
          excludeStarredNote: "Files marked with a star will be kept"
        },
        actions: {
          save: "Save configuration",
          saving: "Saving...",
          runNow: "Run now",
          runNowDescription: "Run a manual cleanup immediately with current settings",
          running: "Cleanup in progress...",
          cancel: "Cancel"
        },
        jobHistory: {
          title: "Cleanup history",
          noHistory: "No history available",
          columns: {
            filesDeleted: "Files deleted",
            spaceFreed: "Space freed"
          },
          status: {
            pending: "Pending",
            processing: "Processing",
            completed: "Completed",
            failed: "Failed",
            retry: "Retrying"
          },
          details: {
            manual: "Manual",
            scheduled: "Scheduled",
            filesDeleted: "{{count}} file(s) deleted",
            spaceFreed: "{{size}} freed",
            duration: "Duration: {{time}}",
            error: "Error: {{message}}"
          }
        },
        success: {
          saved: "Cleanup configuration saved",
          started: "Cleanup started successfully"
        },
        confirmRunNow: "Are you sure you want to run a manual cleanup now?",
        error: {
          save: "Error saving configuration",
          start: "Error starting cleanup",
          load: "Error loading configuration"
        }
      }
    },
    
    company: {
      title: "Company settings",
      subtitle: "Configure your company information that will appear on public job offer pages.",
      name: "Company name",
      namePlaceholder: "Your company name",
      nameNote: "This name will appear on public job offer pages",
      industry: "Industry",
      industryPlaceholder: "Technology, Healthcare, Finance...",
      selectIndustry: "Select an industry",
      logo: {
        title: "Company logo",
        current: "Current logo:",
        change: "Change logo",
        upload: "Upload logo",
        formats: "PNG, JPG, GIF, WebP up to 5MB",
        note: "The logo will appear in the header of public job offer pages. Recommended: square or horizontal rectangle format, transparent background.",
        newSelected: "✓ New file selected: {{name}}",
        remove: "Remove",
        invalidFormat: "Unsupported file format. Use JPG, PNG, GIF or WebP.",
        tooLarge: "File is too large. Maximum size: 5MB.",
        alt: "Company logo"
      },
      preview: {
        title: "Preview on public pages:",
        placeholder: "Company name"
      },
      updateSuccess: "Company settings updated successfully",
      updateError: "Error updating company settings"
    },
    
    notifications: {
      mainTitle: "Notifications",
      mainSubtitle: "Configure who should be notified by email for different platform events.",
      updateSuccess: "Notification settings updated successfully",
      updateError: "Error updating notifications",
      applications: {
        title: "Application notifications",
        description: "Receive notifications when new applications are submitted on your published offers.",
        enabled: "Enable application notifications",
        notifyPublisher: "Notify offer creator",
        notifyPublisherNote: "The creator of each offer will receive notifications",
        customUsers: "Additional users to notify",
        customUsersNote: "These users will receive all application notifications"
      },
      appointments: {
        title: "Appointment notifications",
        description: "Receive notifications when candidates book time slots.",
        enabled: "Enable appointment notifications",
        notifyRecruiter: "Notify the concerned recruiter",
        notifyRecruiterNote: "The recruiter will receive notifications for their appointments",
        customUsers: "Additional users to notify",
        customUsersNote: "These users will receive all appointment notifications"
      },
      analyses: {
        title: "Analysis completion notifications",
        description: "Receive notifications when a CV analysis is completed.",
        enabled: "Enable analysis notifications",
        notifyExecutor: "Notify the analysis executor",
        notifyExecutorNote: "The user who launches the analysis will receive the notification",
        customUsers: "Additional users to notify",
        customUsersNote: "These users will receive all analysis notifications"
      },
      noHrUsers: "No HR users available",
      summary: {
        title: "Configuration summary",
        publisherNotified: "The creator of each offer will be notified",
        recruiterNotified: "The concerned recruiter will be notified",
        executorNotified: "The executor will be notified",
        customUsersNotified: "{{count}} additional user(s)",
        allDisabled: "⚠️ All notifications are disabled"
      },
      // Legacy fields for backward compatibility
      title: "Application notifications",
      subtitle: "Configure who should be notified by email when receiving new applications via published offers.",
      enabled: "Email notifications enabled",
      enabledNote: "Enable/disable all application notifications",
      defaultConfig: "Default configuration",
      notifyPublisher: "Notify offer creator",
      notifyPublisherNote: "By default, whoever publishes an offer receives notifications for that offer",
      customUsers: "Additional users to notify",
      customUsersNote: "Select HR users who should receive notifications for all applications"
    },
    
    usage: {
      title: "Usage and limits",
      currentPlan: "Current plan",
      refresh: "Refresh data",
      expiresOn: "Expires on {{date}}",
      notDefined: "Not defined",
      infiniteLimit: "∞",
      tips: {
        title: "Usage tips",
        resetMonthly: "• Limits reset every month",
        deleteOldCvs: "• Delete old CVs to free up space",
        autoCleanup: "• Use automatic cleanup to optimize storage",
        contactSupport: "• Contact support to increase your limits"
      }
    },
    
    minio: {
      title: "Custom MinIO Storage",
      subtitle: "Configure your own MinIO server for complete control of your data",
      warning: {
        title: "⚠️ Important Warning",
        technical: "• This feature is reserved for technical administrators",
        ownership: "• You are responsible for your MinIO infrastructure and its security",
        costs: "• Hosting MinIO may incur additional costs (server, storage, backups)",
        misconfiguration: "• Incorrect configuration may make your files inaccessible"
      },
      status: {
        title: "Current status",
        hasConfig: "Configuration saved",
        noConfig: "No configuration",
        enabled: "Active storage",
        disabled: "Default Candio storage",
        migration: "Migration: {{status}}"
      },
      config: {
        title: "MinIO Configuration",
        endpoint: "Endpoint",
        endpointPlaceholder: "minio.your-domain.com",
        port: "Port",
        useSSL: "Use SSL",
        accessKey: "Access key",
        secretKey: "Secret key",
        publicUrl: "Public URL (optional)",
        defaultBucket: "Default bucket",
        systemBucket: "System bucket",
        testConnection: "Test connection",
        configure: "Configure MinIO storage",
        save: "Save",
        configSaved: "Configuration saved",
        configuredOn: "Configured on",
        dateUnknown: "Unknown date",
        enable: "Enable",
        disable: "Disable",
        delete: "Delete"
      },
      errors: {
        fillAllFields: "Please fill in all required fields"
      },
      testSuccess: "Valid MinIO configuration! ✅",
      testFailed: "Invalid MinIO configuration",
      testError: "Error testing configuration",
      saveSuccess: "MinIO configuration saved successfully! 🎉",
      saveError: "Error saving configuration",
      enabled: "MinIO configuration enabled!",
      disabled: "MinIO configuration disabled!",
      toggleError: "Error changing status",
      deleteConfirm: "Are you sure you want to delete your custom MinIO configuration?",
      deleteSuccess: "MinIO configuration deleted!",
      deleteError: "Error during deletion",
      migration: {
        title: "Data migration",
        description: "You can migrate your existing data between default Candio storage and your custom MinIO storage.",
        estimate: "Estimate migration",
        toCustom: "Migrate to my storage",
        fromCustom: "Return to Candio storage",
        toCustomConfirm: "Are you sure you want to migrate your data to your custom MinIO storage? This operation may take time.",
        fromCustomConfirm: "Are you sure you want to migrate your data to default Candio storage? This operation may take time.",
        toCustomStarted: "Migration started in background! 🚀",
        fromCustomStarted: "Return migration started in background! 🔄",
        startError: "Error starting migration",
        estimateError: "Error during estimation",
        estimateTitle: "Migration estimate",
        totalFiles: "Files to migrate",
        totalSize: "Total size",
        duration: "Estimated duration: {{duration}}",
        inProgress: "Migration in progress",
        status: "Status:",
        files: "files",
        none: "none"
      }
    },

    aiProviders: {
      title: "AI Providers",
      subtitle: "Configure your API keys to enable AI summaries and analyses",
      openai: {
        title: "OpenAI",
        description: "API key for AI summaries and analyses."
      },
      anthropic: {
        title: "Anthropic",
        description: "API key for advanced analysis with Claude."
      },
      apiKey: "API key",
      apiKeyPlaceholder: "sk-...",
      save: "Save",
      delete: "Remove",
      confirmTitle: "Remove API key",
      updatedAt: "Updated on {{date}}",
      tokenPreview: "Saved key: {{token}}",
      loading: "Loading...",
      note: "If no key is set, the system uses Candio's global keys.",
      confirmDelete: "Remove this API key?",
      messages: {
        saved: "API key saved",
        deleted: "API key removed"
      },
      errors: {
        required: "Please provide a valid API key",
        saveFailed: "Failed to save API key",
        deleteFailed: "Failed to delete API key"
      }
    },
    
    jobs: {
      title: "Background jobs",
      noJobs: "No jobs running",
      refresh: "Refresh",
      loading: "Loading...",
      progress: "Progress",
      created: "Created on {{date}} at {{time}}",
      attempts: "Attempt {{current}}/{{max}}",
      error: "Error:",
      storageMigration: "Storage migration",
      storageCleanup: "Storage cleanup",
      fileTransfer: "File Transfer",
      unknown: "Unknown type"
    },
    
    developer: {
      title: "Developer Configuration",
      techInfo: {
        title: "Technical information",
        containers: "• Your files are stored in private MinIO buckets dedicated to your workspace",
        structure: "• Folder structure follows the format: cvs/, logos/, photos/",
        security: "• Access is secured by temporary signed URLs",
        migration: "• Migration may take time depending on data volume",
        background: "• Migrations run in background to avoid blocking the application"
      }
    },
    
    cleanup: {
      title: "Automatic Cleanup",
      subtitle: "Configure automatic cleanup of obsolete files and data"
    },
    
    billing: {
      title: "Payment & Billing",
      subtitle: "Manage your payment methods and view your invoices.",
      error: {
        load: "Error loading billing data",
        portalUrl: "Unable to access billing portal"
      },
      
      paymentMethods: {
        title: "Payment Methods",
        subtitle: "Manage your credit cards and payment methods",
        manage: "Manage",
        expires: "Expires",
        default: "Default",
        noMethods: "No payment methods on file",
        addMethod: "Add payment method"
      },
      
      invoices: {
        title: "Invoices",
        subtitle: "History of your invoices and payments",
        date: "Date",
        plan: "Plan",
        amount: "Amount",
        statusColumn: "Status",
        status: {
          paid: "Paid",
          pending: "Pending",
          failed: "Failed"
        },
        actions: "Actions",
        download: "Download",
        downloadSuccess: "Invoice downloaded successfully",
        downloadError: "Error downloading invoice",
        noInvoices: "No invoices available",
        subscription: "Subscription"
      }
    }
  },

  // HR Management Page
  rh: {
    title: "HR Management",
    subtitle: "Manage your team's HR user accounts",
    noUsers: "No users found",
    createFirst: "Start by adding an HR user",
    searchPlaceholder: "Search for a user...",
    addUser: "Add HR User",
    
    // Dashboard stats
    stats: {
      hrUsers: "HR Users",
      totalAnalyses: "Total Analyses", 
      createdOffers: "Created Offers",
      uploadedCvs: "Uploaded CVs",
      activeAccounts: "Active Accounts",
      inactiveAccounts: "Inactive Accounts"
    },
    
    // License information
    license: {
      title: "License Information",
      currentPackage: "Current Package",
      status: "Status",
      expiration: "Expiration",
      active: "Active",
      inactive: "Inactive",
      notDefined: "Not defined",
      notDefinedDate: "Not defined"
    },
    
    // Filters
    filters: {
      allStatuses: "All statuses",
      verified: "Verified",
      unverified: "Unverified"
    },
    
    // Table headers
    table: {
      user: "User",
      email: "Email",
      status: "Status",
      createdDate: "Created Date",
      actions: "Actions"
    },
    
    // User status
    userStatus: {
      verified: "Verified",
      unverified: "Unverified",
      hrUser: "HR User",
      nameNotDefined: "Name not defined"
    },
    
    // Pagination
    pagination: {
      previous: "Previous",
      next: "Next",
      showing: "Showing",
      to: "to",
      of: "of",
      results: "results"
    },
    
    // Search and filters help text
    searchHelp: {
      modifySearch: "Try modifying your search criteria"
    },
    
    // Modals
    modals: {
      add: {
        title: "Add HR User",
        fullName: "Full Name",
        emailAddress: "Email Address",
        password: "Password",
        namePlaceholder: "User's name",
        emailPlaceholder: "email@example.com",
        passwordPlaceholder: "Password (min. 8 characters)",
        verificationNote: "A verification email will be sent to the user",
        cancel: "Cancel",
        create: "Create User",
        creating: "Creating..."
      },
      edit: {
        title: "Edit User",
        fullName: "Full Name",
        emailAddress: "Email Address",
        newPassword: "New Password (optional)",
        emailNote: "Changing email will reset verification",
        passwordPlaceholder: "Leave empty to keep unchanged",
        cancel: "Cancel",
        update: "Update",
        updating: "Updating..."
      },
      delete: {
        title: "Delete User",
        confirmText: "Are you sure you want to delete user",
        warningText: "This action is irreversible. The user will no longer be able to access their account.",
        cancel: "Cancel",
        delete: "Delete",
        deleting: "Deleting..."
      }
    },
    
    // Action tooltips
    tooltips: {
      resendVerification: "Resend verification email",
      edit: "Edit",
      delete: "Delete"
    },
    
    // Toast messages
    messages: {
      loadError: "Error loading data",
      fillAllFields: "Please fill all fields",
      fillNameEmail: "Please fill name and email",
      passwordMinLength: "Password must be at least 8 characters",
      userCreated: "HR user created successfully",
      createError: "Error creating user",
      userUpdated: "User updated successfully",
      updateError: "Error updating user",
      userDeleted: "User deleted successfully",
      deleteError: "Error deleting user",
      alreadyVerified: "User is already verified",
      verificationSent: "Verification email sent to",
      emailError: "Error sending email"
    }
  },

  // Appointments system
  appointments: {
    title: "Appointment Scheduling",
    subtitle: "Manage your appointments and configure your availability",
    
    // General errors
    errors: {
      providerAlreadyConfigured: "A video conference provider is already configured. Please delete the existing configuration before setting up a new one.",
      rateLimited: "Too many requests. Please try again in a few minutes.",

      // Backend errors
      serverError: "Server error",
      missingInfo: "Missing information",
      invalidData: "Invalid data",
      notFound: "Not found",
      unauthorized: "Unauthorized",
      bookingNotFound: "Booking page not found",
      bookingNotActive: "Bookings are not enabled for this recruiter",
      datesRequired: "Dates required",
      slotNotAvailable: "This slot is no longer available",
      bookingCreationError: "Error creating appointment",
      invalidToken: "Invalid or already used token",
      appointmentAlreadyCancelled: "Invalid token or appointment already cancelled",
      invalidTimeFormat: "Invalid time format. Use HH:MM",
      endTimeBeforeStart: "End time must be after start time",
      invalidDayOfWeek: "Day of week must be between 0 and 6",
      overlappingSlots: "This slot overlaps with existing slots",
      availabilityNotFound: "Availability not found",
      availabilityFetchError: "Error fetching availabilities",
      availabilityCreateError: "Error creating availability",
      availabilityUpdateError: "Error updating availability",
      availabilityDeleteError: "Error deleting availability",
      invalidProvider: "Invalid provider. Choose from: zoom, meet, teams, slack",
      videoNotConfigured: "Video conferencing is not configured for this workspace",
      zoomKeysNotConfigured: "Zoom API keys are not configured",
      unsupportedProvider: "Unsupported video conferencing provider",
      meetingLinkError: "Error generating meeting link",
      videoSettingsFetchError: "Error fetching settings",
      videoSettingsSaveError: "Error saving settings",
      videoSettingsDeleteError: "Error deleting configuration",
      slugAlreadyTaken: "This URL is already taken",
      slugValidationError: "Invalid URL",
      slugSaveError: "Error saving URL",
      tokenValidationError: "Invalid token",
      tokenSaveError: "Error saving token",
      invalidDuration: "Invalid duration",
      invalidSlug: "Invalid URL",
      settingsFetchError: "Error loading settings",
      settingsSaveError: "Error saving settings",
      holidayNameRequired: "Holiday name is required",
      holidayNotFound: "Holiday not found",
      holidayAlreadyExists: "A holiday already exists on this date",
      holidayFetchError: "Error loading holidays",
      holidayCreateError: "Error adding holiday",
      holidayDeleteError: "Error deleting holiday",
      appointmentNotFound: "Appointment not found",
      appointmentFetchError: "Error loading appointments",
      appointmentConfirmError: "Error confirming appointment",
      appointmentCancelError: "Error cancelling appointment",
      meetingUrlRequired: "Meeting URL is required",
      adminVideoConfigRequired: "Please configure your meeting tool in Settings > Meeting Integration.",
      userVideoConfigRequired: "Please contact your administrator to enable video conferencing tools integration.",
      noBookingUrl: "Please configure your booking URL in settings",
      invitationSendError: "Error sending invitation",
      candidateNotFound: "Candidate not found",
      emailSendError: "Error sending email",
      templateFetchError: "Error loading templates",
      templateSaveError: "Error saving template",
      templateResetError: "Error resetting template"
    },
    
    // Navigation tabs
    tabs: {
      calendar: "My Calendar",
      calendarDesc: "View and manage your appointments",
      availability: "Availability",
      availabilityDesc: "Configure your time slots",
      settings: "Settings",
      settingsDesc: "Appointment duration, delays, booking URL",
      templates: "Email Templates",
      templatesDesc: "Customize your appointment emails",
      holidays: "Holidays",
      holidaysDesc: "Manage your days off",
      video: "Video Conference",
      videoDesc: "Configure your meeting tools"
    },
    
    // Status
    status: {
      pending: "Pending",
      confirmed: "Confirmed",
      cancelled: "Cancelled"
    },
    
    // Filters
    filters: {
      upcoming: "Upcoming",
      confirmed: "Confirmed",
      cancelled: "Cancelled"
    },
    
    // Calendar component
    calendar: {
      today: "Today",
      viewDay: "Day",
      viewWeek: "Week",
      viewMonth: "Month",
      previous: "Previous",
      next: "Next",
      scopeMy: "My calendar",
      scopeTeam: "Team calendar",
      filterRecruiter: "Filter",
      filterAll: "All recruiters",
      recruiter: "Recruiter",
      densityComfortable: "Comfort",
      densityCompact: "Compact",
      appointmentDetails: "Appointment Details",
      candidate: "Candidate",
      online: "Online",
      physical: "In-person",
      joinMeeting: "Join Meeting",
      cancellationReason: "Cancellation Reason",
      confirm: "Confirm",
      cancel: "Cancel",
      cancelReason: "Cancellation reason (optional):",
      bookingUrl: "Your booking link",
      dayView: "Appointments for",
      appointment: "appointment",
      appointments: "appointments",
      inPerson: "In-person",
      noAppointments: "No appointments for this date",
      error: {
        fetch: "Error loading appointments",
        confirm: "Error confirming appointment",
        cancel: "Error cancelling appointment",
        forbidden: "You don't have access to these appointments",
        noVideoAdmin: "Please configure your meeting tool in Settings > Meeting Integration.",
        noVideoUser: "Please contact your administrator to enable video conferencing tools integration.",
        urlRequired: "Please provide the meeting URL"
      },
      manualUrlRequired: "Please provide the meeting URL",
      urlHint: "This URL will be sent to the candidate in the confirmation email",
      success: {
        confirm: "Appointment confirmed successfully",
        cancel: "Appointment cancelled",
        copyUrl: "Link copied to clipboard"
      },
      modalTip: "Click on an appointment or the eye icon to access detailed booking management"
    },
    
    // Recent invitations
    recentInvitations: {
      title: "Recent appointment invitations",
      refreshed: "List updated",
      empty: "No invitations sent recently",
      sentOn: "Sent on",
      error: {
        fetch: "Error loading invitations"
      }
    },
    
    // Availability settings
    availability: {
      title: "My Availability",
      description: "Configure your availability slots for each day of the week",
      days: {
        monday: "Monday",
        tuesday: "Tuesday",
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday"
      },
      add: "Add availability",
      noneForDay: "No availability",
      confirmDelete: "Are you sure you want to delete this availability?",
      confirmDeleteTitle: "Delete Availability",
      addSlot: "Add slot",
      noSlots: "No slots configured",
      error: {
        fetch: "Error loading availability",
        add: "Error adding slot",
        delete: "Error deleting slot",
        toggle: "Error updating slot"
      },
      success: {
        add: "Availability added successfully",
        delete: "Availability deleted successfully",
        toggle: "Slot updated"
      },
      confirm: {
        delete: "Are you sure you want to delete this slot?"
      }
    },
    
    // Booking settings
    settings: {
      title: "Booking Settings",
      description: "Configure appointment booking rules",
      systemStatus: "Booking System",
      statusDescription: "Enable or disable online booking",
      statusActive: "Candidates can book appointments",
      statusInactive: "Bookings are temporarily disabled",
      configTitle: "Slot Configuration",
      configDescription: "Set duration and scheduling rules",
      duration: "Appointment duration",
      durationHelp: "Standard duration for each appointment",
      bufferTime: "Buffer time between appointments",
      bufferHelp: "Automatic break between each appointment",
      noBreak: "No break",
      minAdvance: "Minimum advance booking",
      minAdvanceHelp: "Minimum notice time before appointment",
      maxAdvance: "Maximum advance booking",
      maxAdvanceHelp: "Maximum planning horizon",
      timezone: "Time zone",
      timezoneInfo: "All your slots will be displayed in this time zone",
      useDetected: "Use your detected timezone",
      autoSaving: "Auto-saving...",
      active: "Enable online appointment booking",
      urlTitle: "Booking URL",
      urlDescription: "Customize your appointment booking link",
      customUrl: "Custom URL",
      generateUrl: "Generate random URL",
      checkingAvailability: "Checking availability...",
      urlTaken: "This URL is already taken",
      urlAvailable: "This URL is available",
      yourUrl: "Your booking link",
      shareUrl: "Share this link with your candidates to allow them to book appointments",
      preview: "Preview",
      error: {
        fetch: "Error loading settings",
        save: "Error saving settings",
        generateSlug: "Error generating slug"
      },
      success: {
        save: "Settings saved successfully",
        copy: "Link copied to clipboard"
      }
    },
    
    // Video Configuration Alert
    videoConfigAlert: {
      title: "Video configuration required",
      message: "To enable online appointments, configure a video conferencing service.",
      configure: "Configure now"
    },
    
    // Email templates
    templates: {
      title: "Email Templates",
      description: "Customize automated emails",
      types: {
        invitation: "Appointment Invitation",
        invitationDesc: "Email sent to candidates to invite them to book a slot",
        confirmation: "Appointment Confirmation",
        confirmationDesc: "Email sent automatically after appointment confirmation",
        cancellation: "Appointment Cancellation",
        cancellationDesc: "Email sent when appointment is cancelled"
      },
      availableVars: "Available variables",
      subject: "Subject",
      body: "Message body",
      reset: "Reset",
      preview: "Preview",
      previewTitle: "Email Preview",
      previewNote: "This is a preview with sample data",
      error: {
        fetch: "Error loading templates",
        save: "Error saving template",
        reset: "Error resetting template",
        preview: "Error previewing template"
      },
      success: {
        save: "Template saved successfully",
        reset: "Template reset"
      },
      confirm: {
        reset: "Are you sure you want to reset this template?"
      }
    },
    
    // System email templates (for AdminLanguageContext)
    systemEmailTemplates: {
      invitation: {
        title: "Interview Invitation",
        greeting: "Hello {{candidate_name}},",
        intro: "Thank you for your application and we would like to meet you to discuss your profile.",
        interviewDetails: "Interview Details",
        recruiter: "Recruiter",
        position: "Position",
        company: "Company",
        duration: "Duration",
        type: "Type",
        schedulingInfo: "To schedule your interview, please click on the link below and choose a time slot that suits you:",
        bookingButton: "Choose a time slot",
        importantInfo: "Important information:",
        confirmationNote: "You will receive an email confirmation after choosing your time slot",
        onlineNote: "The video conference link will be sent to you in the confirmation email",
        physicalNote: "The exact address will be communicated to you in the confirmation email",
        regards: "Best regards,",
        team: "The {{company_name}} team"
      },
      confirmation: {
        title: "Appointment Confirmation",
        greeting: "Hello {{candidate_name}},",
        confirmed: "Your appointment has been confirmed!",
        details: "Appointment Details",
        date: "Date",
        time: "Time",
        duration: "Duration",
        type: "Type",
        recruiter: "Recruiter",
        onlineInstructions: "Your appointment will take place online. Join the meeting by clicking on the link below:",
        joinButton: "Join Meeting",
        physicalInstructions: "Your appointment will take place at the following address:",
        preparation: "Preparation",
        prepTips: "Feel free to prepare your questions and review your CV.",
        reschedule: "If you need to reschedule this appointment, please contact us as soon as possible.",
        regards: "Best regards,",
        team: "The {{company_name}} team"
      },
      cancellation: {
        title: "Appointment Cancellation",
        greeting: "Hello {{candidate_name}},",
        cancelled: "Your appointment has been cancelled",
        reason: "Reason",
        noReason: "No specific reason provided",
        reschedule: "If you would like to reschedule an interview, please don't hesitate to contact us.",
        contact: "You can reach us at the following address:",
        apologies: "We apologize for any inconvenience caused.",
        regards: "Best regards,",
        team: "The {{company_name}} team"
      },
      reminder: {
        title: "Appointment Reminder",
        greeting: "Hello {{candidate_name}},",
        reminder: "We remind you that you have a scheduled appointment",
        when: "{{relative_time}}",
        details: "Appointment Details",
        preparation: "Suggested Preparation",
        prepTips: "Review your CV and prepare your questions about the position and company.",
        onlineReminder: "The video conference link is available below:",
        joinButton: "Join Meeting",
        physicalReminder: "Meeting address:",
        regards: "Best regards,",
        team: "The {{company_name}} team"
      },
      recruiterNotification: {
        title: "New Appointment Booked",
        greeting: "Hello {{recruiter_name}},",
        newBooking: "A new appointment has been booked",
        candidateInfo: "Candidate Information",
        name: "Name",
        email: "Email",
        phone: "Phone",
        subject: "Subject",
        appointmentDetails: "Appointment Details",
        date: "Date",
        time: "Time",
        duration: "Duration",
        type: "Type",
        action: "Action Required",
        confirmReminder: "Don't forget to confirm this appointment from your calendar so the candidate receives the video conference link.",
        dashboardButton: "Access Dashboard",
        regards: "Best regards,",
        system: "The Candio System"
      }
    },
    
    // Public booking page (for AdminLanguageContext)
    publicBooking: {
      title: "Book an appointment with",
      selectDate: "Select a date",
      selectTime: "Choose a time slot",
      noSlots: "No slots available",
      selectOtherDate: "Please select another date",
      meetingType: "Meeting type",
      online: "Online",
      onlineDesc: "Video conference",
      physical: "In-person",
      physicalDesc: "Face-to-face meeting",
      yourInfo: "Your information",
      name: "Full name",
      namePlaceholder: "John Doe",
      email: "Email",
      emailPlaceholder: "john.doe@email.com",
      phone: "Phone",
      phonePlaceholder: "+1 234 567 8900",
      subject: "Meeting subject",
      subjectPlaceholder: "Interview for the position of...",
      book: "Book",
      booking: "Booking...",
      successTitle: "Appointment confirmed!",
      successMessage: "You will receive a confirmation email shortly.",
      errorTitle: "Error",
      errorMessage: "An error occurred. Please try again.",
      notFound: "Booking page not found",
      notActive: "Bookings are not enabled for this recruiter",
      required: "Required field",
      invalidEmail: "Invalid email",
      duration: "Duration",
      minutes: "minutes",
      today: "Today",
      tomorrow: "Tomorrow",
      loading: "Loading...",
      meetingTypeFixed: "The meeting type has been set by the recruiter"
    },
    
    // Holiday settings
    holidays: {
      title: "Holidays",
      description: "Manage days when you're not available",
      add: "Add holiday",
      none: "No holidays defined",
      initialize: "Initialize",
      date: "Date",
      name: "Name",
      namePlaceholder: "Holiday name",
      recurring: "Recurring",
      recurringBadge: "Recurring",
      global: "For the whole team",
      empty: "No holidays configured",
      legendGlobal: "Global holiday",
      legendPersonal: "Personal holiday",
      confirmDelete: "Are you sure you want to delete this holiday?",
      confirmDeleteTitle: "Delete Holiday",
      error: {
        fetch: "Error loading holidays",
        add: "Error adding holiday",
        delete: "Error deleting holiday",
        required: "Please fill all fields",
        nameRequired: "Holiday name is required",
        initialize: "Error initializing holidays"
      },
      success: {
        add: "Holiday added successfully",
        delete: "Holiday deleted successfully",
        initialize: "{{count}} holidays added"
      },
      confirm: {
        delete: "Are you sure you want to delete this holiday?",
        deleteTitle: "Delete Holiday",
        initialize: "Do you want to initialize default French holidays?"
      }
    },
    
    // Video conference settings
    video: {
      title: "Video Conference Configuration",
      description: "Configure your meeting tool for online appointments",
      adminOnly: "Administrator configuration",
      adminOnlyDesc: "These settings apply to all workspace users",
      adminOnlyTitle: "Administrator access required",
      adminOnlyMessage: "Only administrators can configure video conferencing settings.",
      provider: "Video conference provider",
      apiConfig: "API Configuration",
      generalSettings: "General Settings",
      defaultDuration: "Default duration",
      timezone: "Timezone",
      active: "Enable video conferencing for appointments",
      configured: "(Configured)",
      configuredNote: "For security reasons, sensitive values are not displayed. Enter new values only if you want to modify them.",
      enterValue: "Enter value",
      instructions: "Configuration Instructions",
      fields: {
        api_key: "API Key",
        api_secret: "API Secret",
        refresh_token: "Refresh Token",
        webhook_secret: "Webhook Secret",
        clientId: "Client ID",
        clientSecret: "Client Secret",
        accountId: "Account ID",
        tenantId: "Tenant ID",
        botToken: "Bot Token",
        appId: "App ID"
      },
      testMeeting: "Test creation",
      authorizeGoogle: "Authorize Google Meet",
      googleAuthorized: "Google Meet is authorized and ready to use",
      meetInstructions: "Google Meet automatically generates links. No API configuration required.",
      meetInstructions1: "Access Google Cloud Console",
      meetInstructions2: "Create a new project or select an existing one",
      meetInstructions3: "Enable Google Calendar API and Google Meet API",
      meetInstructions4: "Create OAuth 2.0 credentials",
      meetInstructions5: "Add authorized redirect URIs",
      zoomInstructions1: "Log in to your Zoom Marketplace account",
      zoomInstructions2: "Create an OAuth Server-to-Server app",
      zoomInstructions3: "Add meeting:write and meeting:read scopes",
      zoomInstructions4: "Copy the Account ID, Client ID and Client Secret",
      teamsInstructions1: "Access Azure Active Directory",
      teamsInstructions2: "Register a new application",
      teamsInstructions3: "Configure permissions: OnlineMeetings.ReadWrite",
      teamsInstructions4: "Create a client secret",
      teamsInstructions5: "Note the Tenant ID and Client ID",
      slackInstructions1: "Create a Slack app",
      slackInstructions2: "Add scopes: calls:write, calls:read",
      slackInstructions3: "Install the app in your workspace",
      slackInstructions4: "Copy the Bot User OAuth Token",
      error: {
        fetch: "Error loading settings",
        save: "Error saving settings",
        delete: "Error deleting configuration",
        noProvider: "Please select a provider",
        missingFields: "Please fill all required fields",
        googleAuth: "Error during Google authentication",
        googleCallback: "Error during authentication",
        testMeeting: "Error during test"
      },
      success: {
        save: "Settings saved successfully",
        delete: "Configuration deleted",
        googleAuth: "Google Meet authenticated successfully",
        testMeeting: "Test meeting created successfully"
      },
      confirm: {
        deleteTitle: "Delete configuration",
        delete: "Are you sure you want to delete this configuration?"
      }
    },
    
    // Appointment invitation modal
    invite: {
      title: "Send Invitation",
      subtitle: "Invite {{name}} to book an appointment",
      meetingType: "Meeting type",
      physical: "In-person",
      physicalDesc: "Face-to-face meeting",
      online: "Online",
      onlineDesc: "Video conference",
      emailPreview: "Email preview",
      subject: "Subject",
      body: "Message",
      sendTo: "Send to",
      position: "Position",
      send: "Send invitation",
      error: {
        noUrl: "Please configure your booking URL in settings",
        noType: "Please select meeting type",
        noVideoAdmin: "Please configure your meeting tool in Settings > Meeting Integration.",
        noVideoUser: "Please contact your administrator to enable video conferencing tools integration.",
        send: "Error sending invitation"
      },
      warning: {
        noVideoAdmin: "Warning: No video conferencing tool configured. Configure it in settings.",
        noVideoUser: "Warning: Video conferencing is not configured. Contact your administrator."
      }
    },
    
    // Send appointment modal
    sendModal: {
      title: "Send appointment invitation",
      bookingActive: "Your bookings are activated",
      bookingInactive: "Bookings not activated",
      bookingUrl: "Booking URL",
      bookingNotConfigured: "Booking URL not configured",
      configureBookingUrl: "You must configure and activate your booking URL in settings before sending invitations",
      goToSettings: "Go to settings",
      activateBooking: "Activate bookings in settings to allow candidates to schedule appointments",
      meetingType: "Meeting type",
      letCandidateChoose: "Let them choose",
      candidateWillChoose: "Candidate will choose",
      online: "Online",
      physical: "In-person",
      noVideoConfig: "No video conferencing service configured",
      configureNow: "Configure now",
      candidateName: "Candidate name",
      candidateNamePlaceholder: "John Doe",
      candidateEmail: "Candidate email",
      candidateEmailPlaceholder: "john.doe@email.com",
      subject: "Subject",
      subjectPlaceholder: "Interview invitation",
      message: "Personalized message",
      messagePlaceholder: "Hello, we would like to meet you to discuss your application...",
      infoTitle: "Important information:",
      info1: "If you choose the meeting type, the candidate cannot change it",
      info2: "If you let the candidate choose, they can select between online and in-person",
      info3: "For online meetings, make sure you have configured a video conferencing service",
      send: "Send invitation",
      success: "Invitation sent successfully",
      errors: {
        missingInfo: "Please fill in all required fields",
        bookingNotConfigured: "Booking URL is not configured. Please update your settings first.",
        noVideoConfig: "Please check the box 'I will provide the meeting URL manually' to continue",
        invalidEmail: "Candidate email is invalid.",
        invalidMeetingType: "Meeting type is invalid.",
        invalidOffer: "Selected offer is invalid.",
        invalidCv: "Selected CV is invalid.",
        sendFailed: "Error sending invitation"
      },
      manualUrlRequired: "You will need to manually provide the meeting URL when confirming the appointment.",
      confirmManualUrl: "I will provide the meeting URL manually",
      configureAuto: "Configure automatic generation",
      videoConfigured: "Video conferencing configured",
      autoUrlGeneration: "The meeting URL will be generated automatically upon confirmation."
    },
  },
  // Super Admin
  superadmin: {
    dashboard: {
      title: "Super Admin Dashboard",
      subtitle: "CV Analyzer platform overview",
      newUsers7d: "New users (7 days)",
      envConfigured: "Configured variables",
      modulesTitle: "Platform modules",
      analyses: "analyses"
    },
    stats: {
      totalUsers: "Total Users",
      administrators: "Administrators",
      activeSubscriptions: "Active Subscriptions",
      admins: "admins",
      rh: "HR"
    },
    admins: {
      title: "Administrators Management",
      subtitle: "Click on an administrator to view their HR users",
      rhUsers: "HR users",
      plan: "Plan"
    },
    users: {
      title: "Users Management",
      subtitle: "Manage administrators and their HR collaborators",
      searchPlaceholder: "Search by name or email",
      resultsCount: "Results",
      noResults: "No administrators found",
      rhUsersTitle: "HR users",
      noRhUsers: "No HR users linked to this admin",
      activate: "Activate",
      deactivate: "Deactivate",
      delete: "Delete",
      deleteBlocked: "Remove HR users before deleting this admin",
      deleteConfirmTitle: "Delete user",
      deleteConfirmMessage: "Are you sure you want to delete {{userName}}? This action is permanent.",
      stats: {
        admins: "Admins",
        activeAdmins: "Active admins",
        rhUsers: "HR users",
        activeRh: "Active HR"
      }
    },
    actions: {
      userManagement: "User management",
      userDescription: "Manage admins and HR users",
      variables: "Environment variables",
      variablesDescription: "Configure critical runtime keys",
      analytics: "Analytics",
      analyticsDescription: "Detailed platform statistics"
    },
    community: {
      title: "Community waitlist",
      subtitle: "Emails captured from the public launch section",
      searchPlaceholder: "Search by email",
      empty: "No waitlist emails found",
      stats: {
        total: "Total emails",
        newLast7Days: "Last 7 days"
      },
      table: {
        email: "Email",
        language: "Language",
        source: "Source",
        createdAt: "Created at"
      },
      errors: {
        load: "Error while loading community waitlist"
      }
    },
    errors: {
      loadStats: "Error loading statistics",
      loadAdmins: "Error loading administrators",
      updateStatus: "Error updating status",
      deleteUser: "Error deleting user"
    },
    success: {
      statusUpdated: "User status updated",
      userDeleted: "User deleted successfully"
    },
    confirm: {
      deleteUser: "Are you sure you want to delete this user?"
    },
    env: {
      title: "Environment variables",
      subtitle: "Configure critical keys (Storage, AI, Email).",
      categories: {
        urls: "URLs",
        core: "Core",
        storage: "Candio Storage",
        email: "System Email",
        ai: "AI",
        video: "Video",
        other: "Other"
      },
      labels: {
        secret: "Secret",
        plain: "Public"
      },
      actions: {
        add: "Add",
        showValue: "Show value",
        hideValue: "Hide value"
      },
      search: "Search",
      searchPlaceholder: "Search key or description...",
      fields: {
        key: "Key",
        selectKey: "Select a key",
        value: "Value",
        category: "Category"
      },
      status: {
        configured: "Configured",
        missing: "Missing"
      },
      stats: {
        total: "Total variables",
        configured: "Configured",
        missing: "Missing",
        secret: "Secret keys"
      },
      empty: "No variables found for this filter",
      table: {
        key: "Key",
        category: "Category",
        status: "Status",
        preview: "Preview",
        actions: "Actions"
      },
      modal: {
        create: "Add variable",
        edit: "Edit variable",
        helper: "Configure a critical environment variable"
      },
      errors: {
        load: "Error loading variables",
        loadSingle: "Unable to load variable",
        missing: "Key and value are required",
        save: "Error saving variable",
        delete: "Error deleting variable"
      },
      success: {
        saved: "Variable saved",
        updated: "Variable updated",
        deleted: "Variable deleted",
        refreshed: "Variables refreshed"
      },
      confirmTitle: "Confirm deletion",
      confirmDelete: "Delete this variable? This action is irreversible.",
      groupSummary: "Public: {{public}} • Secret: {{secret}}"
    }
  },
  // CV Preview
  cvPreview: {
    title: "CV Preview",
    addedOn: "Added on",
    poweredBy: "PDF Preview • Powered by PDF.js",
    zoomOut: "Zoom out",
    zoomIn: "Zoom in",
    resetZoom: "Reset zoom",
    docxFile: "DOCX File",
    docxNotSupported: "Direct preview of DOCX files is not yet available.",
    docxPreview: "DOCX content preview",
    loading: "Loading...",
    loadingPdf: "Loading PDF...",
    retrying: "Retrying...",
    retry: "Retry",
    downloadCta: "Download CV",
    extractedContent: "Extracted content:",
    textNotExtracted: "Text has not yet been extracted from this file.",
    downloadFile: "Download file",
    oldDocFormat: "DOC File (old format)",
    oldDocNotSupported: "Preview of .DOC files is not supported. Please download the file to view it.",
    unsupportedFormat: "Unsupported format",
    unsupportedMessage: "This file type cannot be previewed.",
    errors: {
      loadFailed: "Unable to load the PDF",
      pdfCorrupted: "The PDF file is corrupted or invalid",
      pdfJsUnavailable: "PDF.js is not available",
      network: "Network error while loading PDF",
      passwordProtected: "This PDF is password protected",
      configuration: "PDF.js configuration error - please try again",
      fileAccess: "Unable to access the PDF file on the server",
      generic: "Error while loading: {{message}}"
    }
  },

  // Candidate Modal
  candidateModal: {
    title: {
      edit: "Edit candidate",
      create: "Create candidate profile"
    },
    subtitle: "Complete the candidate profile with clear and actionable information for your HR pipeline.",
    sections: {
      identity: "Identity",
      positioning: "Positioning"
    },
    fields: {
      photo: "Photo",
      photoHelp: "PNG, JPG, GIF up to 5MB",
      firstName: "First name",
      lastName: "Last name",
      title: "Title / Profile",
      specialty: "Specialty",
      score: "Score (0-100)",
      careerSummary: "Career summary",
      strengths: "Strengths",
      weaknesses: "Areas for improvement"
    },
    placeholders: {
      title: "Ex: Senior Full Stack Developer",
      specialty: "Ex: React, Node.js, AWS",
      careerSummary: "Briefly describe the candidate's professional background...",
      strengths: "List the candidate's main strengths...",
      weaknesses: "Identify potential improvement areas..."
    },
    buttons: {
      cancel: "Cancel",
      create: "Create",
      update: "Update",
      saving: "Saving..."
    },
    success: {
      created: "Candidate created successfully",
      updated: "Candidate updated successfully"
    },
    errors: {
      photoSize: "Photo must not exceed 5 MB",
      saveFailed: "Error while saving"
    }
  },

  // AI configuration guard
  aiConfig: {
    modal: {
      title: "AI configuration required",
      message: "AI provider tokens are not configured. Add your API keys in Settings > Developer > AI Providers to enable this feature.",
      goToSettings: "Go to settings",
      cancel: "Close"
    },
    errors: {
      invalidToken: "Invalid AI API key. Please verify your tokens in Settings > Developer > AI Providers."
    }
  },

  // License
  license: {
    expired: {
      message: "Your license has expired. Renew your subscription to continue using all features.",
      rhMessage: "Your administrator's plan has expired. Please contact your administrator to renew the subscription.",
      renew: "Renew"
    }
  },

  // Payment
  payment: {
    success: {
      planUpdated: "Plan updated to {{planName}}!",
      processed: "Payment processed successfully!",
      subscriptionActivated: "Subscription activated successfully!"
    },
    processing: {
      finalizing: "Finalizing your subscription...",
      finalInProgress: "Finalization in progress... Please wait a moment.",
      updateInProgress: "Update in progress... Please refresh the page in a few moments."
    },
    error: {
      syncError: "Synchronization error. Please refresh the page."
    }
  },

  // Beta Launch Modal
  betaLaunch: {
    title: "Beta Plan Coming Soon",
    message: "Currently, only the demo plan is available.",
    comingSoon: "Paid plans will be activated in a few days!",
    feedback: "Your feedback and suggestions are greatly appreciated and help us improve Candio.",
    contact: "Contact Us"
  }
};
